import ConfigurationOptions from '../layouts/ConfigurationOptions.jsx';
import ConfigurationTableSearch, { parameterTableRows } from './ConfigurationTableSearch';
import ClassifierFields, { classifierSettings, classifierError, classifierOutputOptions } from './ClassifierFields';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import server from '../../api/server.js';
import "../loader.css";
import { Info, Save, Upload, Download, Wand2, Archive } from 'lucide-react';
import { FaRegEdit, FaTimes } from "react-icons/fa";
import { TbRestore } from 'react-icons/tb';
import ModernTimeDropdown from '../layouts/modernDropdown.jsx';
import FeedbackSection from '../layouts/FeedbackSection.jsx';
import { IoChatbubbleOutline } from 'react-icons/io5';
import { useToast } from '../../context/ToastContext.jsx';
import SampleTestModal from '../layouts/SampleTestModal.jsx';
import DeployConfirmationModal from '../layouts/DeployConfirmationModal.jsx';
import { getApiToastMessage } from '../../utils/apiToastMessage.js';
import { findBoundarySpaceName, hasBoundarySpace, getBoundarySpaceNameError } from '../../utils/moduleNameValidation.js';
import ConfigurationDiffButton from '../layouts/ConfigurationDiffButton.jsx';

const normalizeOutputTypeForFrontend = (type) => {
  if (type === 'Categorical') return 'Categorical - Single Value';
  if (type === 'Date And Time') return 'Date and Time';
  return type;
};

const normalizeOutputTypeForBackend = (type, fallback = 'Text') => {
  if (type === 'Categorical - Single Value') return 'Categorical';
  if (type === 'Date And Time') return 'Date and Time';
  return type || fallback;
};

const callTypeOptions = [
  ['run_for_non_meaningful_calls', 'Non-Meaningful calls'],
  ['run_for_not_connected_calls', 'Not Connected calls'],
];
const callTypeSettings = (value = {}) => Object.fromEntries(
  callTypeOptions.filter(([key]) => value[key] !== undefined).map(([key]) => [key, value[key]])
);

const moduleCallTypeSettings = (value = {}) => Object.fromEntries(
  callTypeOptions.map(([key]) => [key, value[key] ?? 1])
);

const configWithExecutionSettings = (configuration, settings) => ({
  configuration,
  ...moduleCallTypeSettings(settings),
});

const CustomModuleConfigure = ({ clientID, serviceID, module_name = 'custom_module', setCloseModal, config_data }) => {
  const [moduleExecution, setModuleExecution] = useState(moduleCallTypeSettings());
  const [loadedModuleExecution, setLoadedModuleExecution] = useState(moduleCallTypeSettings());
  const [parameterExecution, setParameterExecution] = useState({});
  const [tableSearch, setTableSearch] = useState({});
  const { showToast } = useToast();

  // Global/Meta state
  const [email, setEmail] = useState(null);
  const [client_id, setClient_id] = useState(null);
  const [service_id, setService_id] = useState(null);
  const [moduleActive, setModuleActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeployConfirmation, setShowDeployConfirmation] = useState(false);
  const [current_version, setCurrent_version] = useState(0)
  const [logicLoading, setLogicLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [expandedSample, setExpandedSample] = useState(null);
  const [showMoreMap, setShowMoreMap] = useState({});
  const [showSampleTest, setShowSampleTest] = useState(false);
  const [showSampleTestModal, setShowSampleTestModal] = useState(false);
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  const [configType, setConfigType] = useState('Live Version');
  const [moduleDraftSaveConfiguration, setModuleDraftSaveConfiguration] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [defaultConfig, setDefaultConfig] = useState(null);
  const [custParamVariablesUsed, setCustParamVariablesUsed] = useState([]);
  const [convSubSaveLoading, setConvSubSaveLoading] = useState(false);
  const [custParamOutputType, setCustParamOutputType] = useState('Text');

  // Tag state (customer parameters only)
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [custParamTag, setCustParamTag] = useState('');
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [isAddingNewTag, setIsAddingNewTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState('');

  // Conversation side
  const [convSubparameters, setConvSubparameters] = useState([]);
  const [convParameters, setConvParameters] = useState([]);

  // Customer side
  const [custSubparameters, setCustSubparameters] = useState([]);
  const [custParameters, setCustParameters] = useState([]);

  // UI tabs
  const [sideTab, setSideTab] = useState('conversation'); // 'conversation' | 'customer'
  const [convInnerTab, setConvInnerTab] = useState('parameters'); // 'subparameters' | 'parameters'
  const [custInnerTab, setCustInnerTab] = useState('parameters'); // 'subparameters' | 'parameters'

  const [showArchivedConvSubparameters, setShowArchivedConvSubparameters] = useState(false);
  const [showArchivedConvParameters, setShowArchivedConvParameters] = useState(false);
  const [showArchivedCustSubparameters, setShowArchivedCustSubparameters] = useState(false);
  const [showArchivedCustParameters, setShowArchivedCustParameters] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveItem, setArchiveItem] = useState(null);
  const [archiveType, setArchiveType] = useState(null); // conv_parameter | conv_subparameter | cust_parameter | cust_subparameter

  // Common form helpers
  const toVariableName = (label) => {
    return (label || '').trim().toLowerCase().replace(/[\s-\/\&]+/g, '_');
  };
  const isActiveCfgItem = (x) => !x?.status || x.status === 'active' || x.status === 'inactive';
  const filterTableRows = (items, showArchived) =>
    showArchived ? (items || []).filter((i) => i.status === 'archived') : (items || []).filter(isActiveCfgItem);
  function getCookie(name) {
    function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
    var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : null;
  }

  const closeModal = () => {
    setCloseModal();
  };

  // Conversation Subparameter modal
  const [showConvSubparamModal, setShowConvSubparamModal] = useState(false);
  const [editingConvSubparam, setEditingConvSubparam] = useState(null);
  const [convSubparamName, setConvSubparamName] = useState('');
  const [convSubparamDefinition, setConvSubparamDefinition] = useState('');
  const [contextScope, setContextScope] = useState('conversation');
  const [classification, setClassification] = useState({});
  const [convSubparamType, setConvSubparamType] = useState('Yes/No/NA');
  const [convSubparamPossibleValues, setConvSubparamPossibleValues] = useState([]);
  const [convSubparamCurrentTag, setConvSubparamCurrentTag] = useState('');
  const convSubparamFormRef = useRef(null);

  // Conversation Parameter modal
  const [showConvParamModal, setShowConvParamModal] = useState(false);
  const [isEditConvParam, setIsEditConvParam] = useState(false);
  const [editingConvParamId, setEditingConvParamId] = useState(null);
  const [convParamName, setConvParamName] = useState('');
  const [convParamSelectedSubs, setConvParamSelectedSubs] = useState([]);
  const [convParamManualQuery, setConvParamManualQuery] = useState('');
  const [convParamLogicExplanation, setConvParamLogicExplanation] = useState('');
  const [convParamQueryError, setConvParamQueryError] = useState('');
  const [writeWithAIConvParam, setWriteWithAIConvParam] = useState(false);
  const convParamFormRef = useRef(null);
  const [allowedConvSubVars, setAllowedConvSubVars] = useState([]); // computed list of var names
  const [convParamOutputType, setConvParamOutputType] = useState('Text');
  const [convParamSelectedSubparam, setConvParamSelectedSubparam] = useState('');
  const [convParamShowDropdown, setConvParamShowDropdown] = useState(false);
  const convParamDropdownRef = useRef(null);
  const convParamInputRef = useRef(null);
  const filteredConvSubVars = useMemo(() => {
    return (allowedConvSubVars || []).filter(
      n => n.toLowerCase().includes((convParamSelectedSubparam || '').toLowerCase()) &&
      !(convParamSelectedSubs || []).includes(n)
    );
  }, [allowedConvSubVars, convParamSelectedSubparam, convParamSelectedSubs]);
  const addConvSubparamToQuery = () => {
    const name = (convParamSelectedSubparam || '').trim();
    if (!name) return;
    if (allowedConvSubVars.includes(name) && !convParamSelectedSubs.includes(name)) {
      setConvParamSelectedSubs(prev => [...prev, name]);
      setConvParamSelectedSubparam('');
      setConvParamShowDropdown(false);
    }
  };
  const removeConvSubparamFromQuery = (name) => {
    setConvParamSelectedSubs(prev => prev.filter(n => n !== name));
  };
  useEffect(() => {
    const onClickOutside = (e) => {
      if (convParamDropdownRef.current && !convParamDropdownRef.current.contains(e.target) &&
          convParamInputRef.current && !convParamInputRef.current.contains(e.target)) {
        setConvParamShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Customer Subparameter modal
  const [showCustSubparamModal, setShowCustSubparamModal] = useState(false);
  const [editingCustSubparam, setEditingCustSubparam] = useState(null);
  const [custSubparamName, setCustSubparamName] = useState('');
  const [custSubparamDefinition, setCustSubparamDefinition] = useState('');
  const [custSubparamType, setCustSubparamType] = useState('Text'); // hidden from UI, default Text
  const [custSubparamPossibleValues, setCustSubparamPossibleValues] = useState([]);
  const [custSubparamCurrentTag, setCustSubparamCurrentTag] = useState('');
  const [custSubparamManualQuery, setCustSubparamManualQuery] = useState('');
  const [custSubparamLogicExplanation, setCustSubparamLogicExplanation] = useState('');
  const [custSubparamQueryError, setCustSubparamQueryError] = useState('');
  const [writeWithAICustSubparam, setWriteWithAICustSubparam] = useState(false);
  const custSubparamFormRef = useRef(null);
  const [custSubparamSelectedVars, setCustSubparamSelectedVars] = useState([]);
  const [custSubparamSelectedVar, setCustSubparamSelectedVar] = useState('');
  const [custSubparamShowDropdown, setCustSubparamShowDropdown] = useState(false);
  const custSubparamDropdownRef = useRef(null);
  const custSubparamInputRef = useRef(null);
  

  // Customer Parameter modal
  const [showCustParamModal, setShowCustParamModal] = useState(false);
  const [isEditCustParam, setIsEditCustParam] = useState(false);
  const [editingCustParamId, setEditingCustParamId] = useState(null);
  const [custParamName, setCustParamName] = useState('');
  const [custParamManualQuery, setCustParamManualQuery] = useState('');
  const [custParamLogicExplanation, setCustParamLogicExplanation] = useState('');
  const [custParamQueryError, setCustParamQueryError] = useState('');
  const [writeWithAICustParam, setWriteWithAICustParam] = useState(false);
  const custParamFormRef = useRef(null);
  const [custParamSelectedVars, setCustParamSelectedVars] = useState([]);
  const [custParamSelectedVar, setCustParamSelectedVar] = useState('');
  const [custParamShowDropdown, setCustParamShowDropdown] = useState(false);
  const custParamDropdownRef = useRef(null);
  const custParamInputRef = useRef(null);

  // Audio/chat preview helpers
  const audioRef = useRef(null);
  const segmentRefs = useRef([]);
  const sourceClickInProgressRef = useRef(false);
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState(null);

  const toggleShowMore = (key) => setShowMoreMap(prev => ({ ...prev, [key]: !prev[key] }));

  // Derived variable name sets
  const convSubVars = useMemo(() => (convSubparameters || []).filter((s) => s.status !== 'archived').map((s) => toVariableName(s.display_name)), [convSubparameters]);
  const convParamVars = useMemo(() => (convParameters || []).filter((p) => p.status !== 'archived').map((p) => toVariableName(p.display_name)), [convParameters]);
  const custSubVars = useMemo(() => (custSubparameters || []).filter((s) => s.status !== 'archived').map((s) => toVariableName(s.display_name)), [custSubparameters]);
  const custParamVars = useMemo(() => (custParameters || []).filter((p) => p.status !== 'archived').map((p) => toVariableName(p.display_name)), [custParameters]);
  const systemCustSubVars = useMemo(() => {
    return (custSubparameters || [])
      .filter((s) => !!s?.system && s.status !== 'archived')
      .map((s) => toVariableName(s.display_name));
  }, [custSubparameters]);

  // For Customer Subparameter (logic) context: conversation params + system customer subparams (current) + prev_{custSub}
  const allowedVarsForCustSubparamLogic = useMemo(() => {
    const prevCustSubs = custSubVars.map(v => `prev_${v}`);
    return Array.from(new Set([...convParamVars, ...systemCustSubVars, ...prevCustSubs]));
  }, [convParamVars, systemCustSubVars, custSubVars]);

  // For Customer Parameter logic: present customer subparams + prev_{customer params}
  const allowedVarsForCustParamLogic = useMemo(() => {
    const prevCustParams = custParamVars.map(v => `prev_${v}`);
    return [...custSubVars, ...prevCustParams];
  }, [custSubVars, custParamVars]);

  const filteredCustParamVars = useMemo(() => {
    const q = (custParamSelectedVar || '').toLowerCase();
    return (allowedVarsForCustParamLogic || []).filter(
      n => n.toLowerCase().includes(q) && !(custParamSelectedVars || []).includes(n)
    );
  }, [allowedVarsForCustParamLogic, custParamSelectedVar, custParamSelectedVars]);
  const addCustVarToParam = () => {
    const name = (custParamSelectedVar || '').trim();
    if (!name) return;
    if (allowedVarsForCustParamLogic.includes(name) && !custParamSelectedVars.includes(name)) {
      setCustParamSelectedVars(prev => [...prev, name]);
      setCustParamSelectedVar('');
      setCustParamShowDropdown(false);
    }
  };
  const removeCustVarFromParam = (name) => {
    setCustParamSelectedVars(prev => prev.filter(n => n !== name));
  };
  useEffect(() => {
    const onClickOutside = (e) => {
      if (custParamDropdownRef.current && !custParamDropdownRef.current.contains(e.target) &&
          custParamInputRef.current && !custParamInputRef.current.contains(e.target)) {
        setCustParamShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Customer Subparameter variable selector helpers (must come after allowedVarsForCustSubparamLogic)
  const filteredCustVars = useMemo(() => {
    const q = (custSubparamSelectedVar || '').toLowerCase();
    return (allowedVarsForCustSubparamLogic || []).filter(
      n => n.toLowerCase().includes(q) && !(custSubparamSelectedVars || []).includes(n)
    );
  }, [allowedVarsForCustSubparamLogic, custSubparamSelectedVar, custSubparamSelectedVars]);
  const addCustVarToSubparam = () => {
    const name = (custSubparamSelectedVar || '').trim();
    if (!name) return;
    if (allowedVarsForCustSubparamLogic.includes(name) && !custSubparamSelectedVars.includes(name)) {
      setCustSubparamSelectedVars(prev => [...prev, name]);
      setCustSubparamSelectedVar('');
      setCustSubparamShowDropdown(false);
    }
  };
  const removeCustVarFromSubparam = (name) => {
    setCustSubparamSelectedVars(prev => prev.filter(n => n !== name));
  };
  useEffect(() => {
    const onClickOutside = (e) => {
      if (custSubparamDropdownRef.current && !custSubparamDropdownRef.current.contains(e.target) &&
          custSubparamInputRef.current && !custSubparamInputRef.current.contains(e.target)) {
        setCustSubparamShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // API helpers
  const checkDefinitionValidity = async (definition, type, options) => {
    if (['Categorical - Single Value', 'Categorical - Multiple Values', 'Yes/No/NA', 'Number', 'Text', 'Date and Time'].includes(type)) {
      let res = await server.checkDefinitionValidity(client_id, service_id, email, 'apps', undefined, definition, type, options, module_name);
      let resp = await res.json();
      if (res.status === 200) {
        if (resp.data) {
          if (resp.data.error) return { error: resp.data.error };
          return { error: null };
        }
      } else {
        return { error: resp.error };
      }
    }
    return { error: null };
  };

  // Conversation parameter: verify logic
  const verifyConvParamQuery = async (queryToVerify = null, selectedVars = null) => {
    setLogicLoading(true);
    const query = queryToVerify ?? convParamManualQuery;
    const selected = selectedVars ?? convParamSelectedSubs;
    if (!query.trim()) {
      setLogicLoading(false);
      setConvParamQueryError('Query cannot be empty');
      return false;
    }
    try {
      const expectedReturn = convParamOutputType === 'Number' ? 'numeric' : 'string';
      const matchType = convParamOutputType === 'Number' ? 'exact' : 'subset';
      const result = await server.verifyQuery(
        client_id,
        service_id,
        query,
        selected,
        convSubparameters.filter((s) => s.status !== 'archived'),
        expectedReturn,
        matchType
      );
      if (result.status === 200) {
        const resp = await result.json();
        setConvParamLogicExplanation(resp.logic_explanation || '');
        if (resp.errors && resp.errors.length > 0) {
          setLogicLoading(false);
          setConvParamQueryError(resp.errors[0]['error_type'] + ": " + resp.errors[0]['error_description']);
          return false;
        }
        setLogicLoading(false);
        setConvParamQueryError('');
        setConvParamManualQuery(resp.formatted || query);
        return true;
      } else {
        setLogicLoading(false);
        setConvParamQueryError(result.errorResponse?.error || 'Validation failed');
        return false;
      }
    } catch (e) {
      setLogicLoading(false);
      setConvParamQueryError('Error verifying query');
      return false;
    }
  };

  // Customer subparameter (logic) verify
  const verifyCustSubparamQuery = async (queryToVerify = null) => {
    setLogicLoading(true);
    const query = queryToVerify ?? custSubparamManualQuery;
    if (!query.trim()) {
      setLogicLoading(false);
      setCustSubparamQueryError('Query cannot be empty');
      return false;
    }
    try {
      // Build synthetic list for verification containing:
      // - conversation parameters (current)
      // - system customer subparameters (current)
      // - customer subparameters (previous via prev_{...})
      const syntheticVars = [
        ...convParameters.filter((p) => p.status !== 'archived').map(p => ({
          display_name: toVariableName(p.display_name),
          output_type: p?.output_type === 'Number' ? 'Number' : 'Text',
          possible_values: []
        })),
        ...(custSubparameters || [])
          .filter(s => !!s?.system && s.status !== 'archived')
          .map(s => ({
            display_name: toVariableName(s.display_name),
            output_type: s?.output_type === 'Number' ? 'Number' : 'Text',
            possible_values: s.possible_values || []
          })),
        ...custSubparameters.filter((s) => s.status !== 'archived').map(s => ({
          display_name: `prev_${toVariableName(s.display_name)}`,
          output_type: s?.output_type === 'Number' ? 'Number' : 'Text',
          possible_values: []
        }))
      ];
      const expectedReturn = custSubparamType === 'Number' ? 'numeric' : 'string';
      const matchType = custSubparamType === 'Number' ? 'exact' : 'subset';
      const result = await server.verifyQuery(
        client_id,
        service_id,
        query,
        custSubparamSelectedVars,
        syntheticVars,
        expectedReturn,
        matchType
      );
      if (result.status === 200) {
        const resp = await result.json();
        setCustSubparamLogicExplanation(resp.logic_explanation || '');
        if (resp.errors && resp.errors.length > 0) {
          setLogicLoading(false);
          setCustSubparamQueryError(resp.errors[0]['error_type'] + ": " + resp.errors[0]['error_description']);
          return false;
        }
        setLogicLoading(false);
        setCustSubparamQueryError('');
        setCustSubparamManualQuery(resp.formatted || query);
        return true;
      } else {
        setLogicLoading(false);
        setCustSubparamQueryError(result.errorResponse?.error || 'Validation failed');
        return false;
      }
    } catch (e) {
      setLogicLoading(false);
      setCustSubparamQueryError('Error verifying query');
      return false;
    }
  };

  // Customer parameter verify
  const verifyCustParamQuery = async (queryToVerify = null) => {
    setLogicLoading(true);
    const query = queryToVerify ?? custParamManualQuery;
    if (!query.trim()) {
      setLogicLoading(false);
      setCustParamQueryError('Query cannot be empty');
      return false;
    }
    try {
      // Build synthetic list for verification containing customer subparameters and prev_{customer params}
      const syntheticVars = [
        ...custSubparameters.filter((s) => s.status !== 'archived').map(s => ({ display_name: toVariableName(s.display_name), output_type: (s.output_type === 'Number' ? 'Number' : 'Text'), possible_values: s.possible_values || [] })),
        ...custParameters.filter((p) => p.status !== 'archived').map(p => ({ display_name: `prev_${toVariableName(p.display_name)}`, output_type: (p.output_type === 'Number' ? 'Number' : 'Text'), possible_values: [] }))
      ];
      const expectedReturn = custParamOutputType === 'Number' ? 'numeric' : 'string';
      const matchType = custParamOutputType === 'Number' ? 'exact' : 'subset';
      const result = await server.verifyQuery(
        client_id,
        service_id,
        query,
        custParamSelectedVars,
        syntheticVars,
        expectedReturn,
        matchType
      );
      if (result.status === 200) {
        const resp = await result.json();
        setCustParamLogicExplanation(resp.logic_explanation || '');
        if (resp.errors && resp.errors.length > 0) {
          setLogicLoading(false);
          setCustParamQueryError(resp.errors[0]['error_type'] + ": " + resp.errors[0]['error_description']);
          return false;
        }
        setLogicLoading(false);
        setCustParamQueryError('');
        setCustParamManualQuery(resp.formatted || query);
        if (Array.isArray(resp.subparameters)) {
          setCustParamVariablesUsed(resp.subparameters);
          setCustParamSelectedVars(resp.subparameters);
        }
        return true;
      } else {
        setLogicLoading(false);
        setCustParamQueryError(result.errorResponse?.error || 'Validation failed');
        return false;
      }
    } catch (e) {
      setLogicLoading(false);
      setCustParamQueryError('Error verifying query');
      return false;
    }
  };

  // AI helpers
  const generateLogicWithAI = async (prompt, setQuery, verifyFn, contextSubparams) => {
    try {
      setLogicLoading(true);
      let res = await server.generateLogicWithAI(client_id, service_id, 'apps', undefined, email, prompt, JSON.stringify(contextSubparams || []));
      if (res.status === 200) {
        const resp = await res.json();
        if (resp.data && resp.data.logic) {
          setQuery(resp.data.logic);
          await verifyFn(resp.data.logic);
        }
      } else {
        setLogicLoading(false);
        await res.json();
        showToast('Failed to generate logic. Please try again later.', 'error');
      }
    } catch (e) {
      setLogicLoading(false);
      showToast('Failed to generate logic. Please try again later.', 'error');
    } finally {
      setLogicLoading(false);
    }
  };

  // Handlers: Conversation Subparameters
  const openAddConvSubparam = () => {
    setEditingConvSubparam(null);
    setConvSubparamName('');
    setConvSubparamDefinition('');
    setContextScope('conversation');
    setParameterExecution({});
    setClassification({});
    setConvSubparamType('Yes/No/NA');
    setConvSubparamPossibleValues([]);
    setConvSubparamCurrentTag('');
    setShowConvSubparamModal(true);
    setTimeout(() => convSubparamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const openEditConvSubparam = (id) => {
    const sub = convSubparameters.find(s => s.id === id);
    if (!sub) return;
    if (sub.system) {
      showToast('System Parameter', 'error');
      return;
    }
    if (sub.status === 'archived') {
      showToast('Restore before editing', 'error');
      return;
    }
    setEditingConvSubparam(sub);
    setConvSubparamName(sub.display_name || '');
    setConvSubparamDefinition(sub.instruction || '');
    setContextScope(sub.context_scope || 'conversation');
    setParameterExecution(callTypeSettings(sub));
    setClassification(classifierSettings(sub));
    setConvSubparamType(sub.output_type || 'Yes/No/NA');
    setConvSubparamPossibleValues(sub.possible_values || []);
    setShowConvSubparamModal(true);
    setTimeout(() => convSubparamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const addConvSubparamTag = () => {
    const t = (convSubparamCurrentTag || '').trim();
    if (!t) return;
    setConvSubparamPossibleValues(prev => prev.includes(t) ? prev : [...prev, t]);
    setConvSubparamCurrentTag('');
  };
  const removeConvSubparamTag = (tag) => {
    setConvSubparamPossibleValues(prev => prev.filter(t => t !== tag));
  };
  const saveConvSubparam = async () => {
    if (!convSubparamName.trim()) {
      showToast('Subparameter name is required', 'error');
      return;
    }
    if (hasBoundarySpace(convSubparamName)) {
      showToast(getBoundarySpaceNameError('Subparameter name', convSubparamName), 'error');
      return;
    }
    setConvSubSaveLoading(true);
    const name = convSubparamName.trim();
    // Restrict reserved prefix for variable key
    const convSubVar = toVariableName(name);
    if (convSubVar.startsWith('prev_')) {
      showToast("Subparameter name cannot start with 'prev_'", 'error');
      setConvSubSaveLoading(false);
      return;
    }
    if (!editingConvSubparam) {
      const exists = convSubparameters.some(s => isActiveCfgItem(s) && (s.display_name || '').toLowerCase() === name.toLowerCase());
      if (exists) {
        showToast('Subparameter name already exists', 'error');
        setConvSubSaveLoading(false);
        return;
      }
    }
    if (classification.subparameter_type !== 'classifier' && ['Categorical - Single Value', 'Categorical - Multiple Values', 'Yes/No/NA', 'Number', 'Date and Time'].includes(convSubparamType)) {
      const flag = await checkDefinitionValidity(convSubparamDefinition, convSubparamType, convSubparamPossibleValues);
      if (flag.error) {
        showToast(flag.error, 'error');
        setConvSubSaveLoading(false);
        return;
      }
    }
    const classifierValue = { ...classification, output_type: convSubparamType, possible_values: convSubparamPossibleValues, instruction: convSubparamDefinition };
    const classificationError = classifierError(classifierValue);
    if (classificationError) {
      showToast(classificationError, 'error');
      setConvSubSaveLoading(false);
      return;
    }
    const newSub = {
      ...classifierSettings(classifierValue),
      id: editingConvSubparam?.id ? editingConvSubparam.id : (convSubparameters.length ? convSubparameters[convSubparameters.length - 1].id + 1 : 1),
      display_name: name,
      instruction: convSubparamDefinition,
      context_scope: contextScope,
      ...parameterExecution,
      output_type: convSubparamType === 'Date and Time' ? 'Date and Time' : convSubparamType,
      possible_values: convSubparamType === 'Yes/No/NA' ? ['Yes', 'No', 'NA'] : (convSubparamPossibleValues || []),
      system: editingConvSubparam?.system || 0,
      status: 'active'
    };
    if (editingConvSubparam?.id) {
      setConvSubparameters(prev => prev.map(s => s.id === editingConvSubparam.id ? newSub : s));
      showToast('Subparameter updated', 'success');
    } else {
      setConvSubparameters(prev => [...prev, newSub]);
      showToast('Subparameter added', 'success');
    }
    setShowConvSubparamModal(false);
    setConvSubSaveLoading(false);
  };

  // Handlers: Conversation Parameters
  const openAddConvParam = () => {
    setIsEditConvParam(false);
    setEditingConvParamId(null);
    setConvParamName('');
    setConvParamSelectedSubs([]);
    setConvParamSelectedSubparam('');
    setConvParamShowDropdown(false);
    setConvParamManualQuery('');
    setConvParamQueryError('');
    setConvParamLogicExplanation('');
    setWriteWithAIConvParam(false);
    setAllowedConvSubVars(convSubVars);
    setConvParamOutputType('Text');
    setShowConvParamModal(true);
    setTimeout(() => convParamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const openEditConvParam = (id) => {
    const p = convParameters.find(x => x.id === id);
    if (!p) return;
    if (p.status === 'archived') {
      showToast('Restore before editing', 'error');
      return;
    }
    setIsEditConvParam(true);
    setEditingConvParamId(id);
    setConvParamName(p.display_name || '');
    setConvParamSelectedSubs(p.subparameters_used || []);
    setConvParamSelectedSubparam('');
    setConvParamShowDropdown(false);
    setConvParamManualQuery(p.logic || '');
    setConvParamLogicExplanation(p.logic_explanation || '');
    setConvParamQueryError('');
    setWriteWithAIConvParam(false);
    setAllowedConvSubVars(convSubVars);
    setConvParamOutputType(p.output_type || 'Text');
    setShowConvParamModal(true);
    setTimeout(() => convParamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const saveConvParameter = async () => {
    if (!convParamName.trim()) return;
    if (hasBoundarySpace(convParamName)) {
      showToast(getBoundarySpaceNameError('Parameter name', convParamName), 'error');
      return;
    }
    // Restrict reserved prefix for variable key
    const convParamVar = toVariableName(convParamName.trim());
    if (convParamVar.startsWith('prev_')) {
      showToast("Parameter name cannot start with 'prev_'", 'error');
      return;
    }
    const ok = await verifyConvParamQuery();
    if (!ok) {
      showToast('Please fix logic before saving', 'error');
      return;
    }
    const name = convParamName.trim();
    if (!isEditConvParam) {
      const exists = convParameters.some(p => isActiveCfgItem(p) && (p.display_name || '').toLowerCase() === name.toLowerCase());
      if (exists) {
        showToast('Parameter name already exists', 'error');
        return;
      }
    }
    const newParam = {
      id: isEditConvParam ? editingConvParamId : (convParameters.length ? convParameters[convParameters.length - 1].id + 1 : 1),
      display_name: name,
      logic: convParamManualQuery,
      subparameters_used: convParamSelectedSubs,
      output_type: convParamOutputType,
      logic_explanation: convParamLogicExplanation || '',
      status: 'active'
    };
    if (isEditConvParam) {
      setConvParameters(prev => prev.map(p => p.id === editingConvParamId ? newParam : p));
      showToast('Parameter updated', 'success');
    } else {
      setConvParameters(prev => [...prev, newParam]);
      showToast('Parameter added', 'success');
    }
    setShowConvParamModal(false);
  };

  // Handlers: Customer Subparameters
  const openAddCustSubparam = () => {
    setEditingCustSubparam(null);
    setCustSubparamName('');
    setCustSubparamDefinition('');
    setCustSubparamType('Text');
    setCustSubparamPossibleValues([]);
    setCustSubparamCurrentTag('');
    setCustSubparamManualQuery('');
    setCustSubparamQueryError('');
    setCustSubparamLogicExplanation('');
    setWriteWithAICustSubparam(false);
    setCustSubparamSelectedVars([]);
    setCustSubparamSelectedVar('');
    setCustSubparamShowDropdown(false);
    setShowCustSubparamModal(true);
    setTimeout(() => custSubparamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const openEditCustSubparam = (id) => {
    const s = custSubparameters.find(x => x.id === id);
    if (!s) return;
    if (s.system) {
      showToast('System Parameter', 'error');
      return;
    }
    if (s.status === 'archived') {
      showToast('Restore before editing', 'error');
      return;
    }
    setEditingCustSubparam(s);
    setCustSubparamName(s.display_name || '');
    setCustSubparamDefinition(s.instruction || '');
    setCustSubparamType(s.output_type || 'Text');
    setCustSubparamPossibleValues(s.possible_values || []);
    setCustSubparamManualQuery(s.logic || '');
    setCustSubparamLogicExplanation(s.logic_explanation || '');
    setCustSubparamQueryError('');
    setWriteWithAICustSubparam(false);
    setCustSubparamSelectedVars(s.variables_used || []);
    setCustSubparamSelectedVar('');
    setCustSubparamShowDropdown(false);
    setShowCustSubparamModal(true);
    setTimeout(() => custSubparamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const addCustSubparamTag = () => {
    const t = (custSubparamCurrentTag || '').trim();
    if (!t) return;
    setCustSubparamPossibleValues(prev => prev.includes(t) ? prev : [...prev, t]);
    setCustSubparamCurrentTag('');
  };
  const removeCustSubparamTag = (tag) => {
    setCustSubparamPossibleValues(prev => prev.filter(t => t !== tag));
  };
  const saveCustSubparam = async () => {
    if (!custSubparamName.trim()) {
      showToast('Subparameter name is required', 'error');
      return;
    }
    if (hasBoundarySpace(custSubparamName)) {
      showToast(getBoundarySpaceNameError('Subparameter name', custSubparamName), 'error');
      return;
    }
    const name = custSubparamName.trim();
    // Restrict reserved prefix for variable key
    const custSubVar = toVariableName(name);
    if (custSubVar.startsWith('prev_')) {
      showToast("Subparameter name cannot start with 'prev_'", 'error');
      return;
    }
    if (!editingCustSubparam) {
      const exists = custSubparameters.some(s => isActiveCfgItem(s) && (s.display_name || '').toLowerCase() === name.toLowerCase());
      if (exists) {
        showToast('Subparameter name already exists', 'error');
        return;
      }
    }
    // Always logic-based for UI-created subparameters
    const ok = await verifyCustSubparamQuery();
    if (!ok) {
      showToast('Please fix logic before saving', 'error');
      return;
    }
    const newSub = {
      id: editingCustSubparam?.id ? editingCustSubparam.id : (custSubparameters.length ? custSubparameters[custSubparameters.length - 1].id + 1 : 1),
      display_name: name,
      instruction: custSubparamDefinition,
      output_type: custSubparamType,
      possible_values: [],
      system: editingCustSubparam?.system || 0,
      status: 'active',
      logic: custSubparamManualQuery,
      logic_explanation: (custSubparamLogicExplanation || ''),
      variables_used: custSubparamSelectedVars
    };
    if (editingCustSubparam?.id) {
      setCustSubparameters(prev => prev.map(s => s.id === editingCustSubparam.id ? newSub : s));
      showToast('Subparameter updated', 'success');
    } else {
      setCustSubparameters(prev => [...prev, newSub]);
      showToast('Subparameter added', 'success');
    }
    setShowCustSubparamModal(false);
  };

  // Handlers: Customer Parameters
  const openAddCustParam = () => {
    setIsEditCustParam(false);
    setEditingCustParamId(null);
    setCustParamName('');
    setCustParamManualQuery('');
    setCustParamLogicExplanation('');
    setCustParamQueryError('');
    setWriteWithAICustParam(false);
    setCustParamOutputType('Text');
    setCustParamSelectedVars([]);
    setCustParamSelectedVar('');
    setCustParamShowDropdown(false);
    setCustParamTag('');
    setIsTagOpen(false);
    setIsAddingNewTag(false);
    setNewTagValue('');
    setShowCustParamModal(true);
    setTimeout(() => custParamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const openEditCustParam = (id) => {
    const p = custParameters.find(x => x.id === id);
    if (!p) return;
    if (p.status === 'archived') {
      showToast('Restore before editing', 'error');
      return;
    }
    setIsEditCustParam(true);
    setEditingCustParamId(id);
    setCustParamName(p.display_name || '');
    setCustParamManualQuery(p.logic || '');
    setCustParamLogicExplanation(p.logic_explanation || '');
    setCustParamQueryError('');
    setWriteWithAICustParam(false);
    setCustParamOutputType(p.output_type || 'Text');
    // Prefer subparameters_used for customer parameters (fallback to legacy variables_used)
    const used = Array.isArray(p.subparameters_used)
      ? p.subparameters_used
      : (Array.isArray(p.variables_used) ? p.variables_used : []);
    setCustParamSelectedVars(used);
    setCustParamSelectedVar('');
    setCustParamShowDropdown(false);
    setCustParamTag(p.tag || '');
    setIsTagOpen(false);
    setIsAddingNewTag(false);
    setNewTagValue('');
    setShowCustParamModal(true);
    setTimeout(() => custParamFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const saveCustParameter = async () => {
    if (!custParamName.trim()) return;
    if (hasBoundarySpace(custParamName)) {
      showToast(getBoundarySpaceNameError('Parameter name', custParamName), 'error');
      return;
    }
    // Restrict reserved prefix for variable key
    const custParamVar = toVariableName(custParamName.trim());
    if (custParamVar.startsWith('prev_')) {
      showToast("Parameter name cannot start with 'prev_'", 'error');
      return;
    }
    const ok = await verifyCustParamQuery();
    if (!ok) {
      showToast('Please fix logic before saving', 'error');
      return;
    }
    const name = custParamName.trim();
    if (!isEditCustParam) {
      const exists = custParameters.some(p => isActiveCfgItem(p) && (p.display_name || '').toLowerCase() === name.toLowerCase());
      if (exists) {
        showToast('Parameter name already exists', 'error');
        return;
      }
    }
    const newParam = {
      id: isEditCustParam ? editingCustParamId : (custParameters.length ? custParameters[custParameters.length - 1].id + 1 : 1),
      display_name: name,
      logic: custParamManualQuery,
      output_type: custParamOutputType,
      logic_explanation: custParamLogicExplanation || '',
      // Use subparameters_used consistently (align with conversation params)
      subparameters_used: Array.isArray(custParamSelectedVars) ? custParamSelectedVars : [],
      status: 'active',
      tag: custParamTag || ''
    };
    if (isEditCustParam) {
      setCustParameters(prev => prev.map(p => p.id === editingCustParamId ? newParam : p));
      showToast('Parameter updated', 'success');
    } else {
      setCustParameters(prev => [...prev, newParam]);
      showToast('Parameter added', 'success');
    }
    setShowCustParamModal(false);
  };

  const handleArchiveConvSub = (sub) => {
    const v = toVariableName(sub.display_name);
    const refP = convParameters.find((p) => p.status !== 'archived' && (p.subparameters_used || []).includes(v));
    const refS = custSubparameters.find((s) => s.status !== 'archived' && !s.system && (s.variables_used || []).includes(v));
    if (sub?.system || refP || refS) return;
    setConvSubparameters((prev) => prev.map((row) => (row.id === sub.id ? { ...row, status: 'archived' } : row)));
    showToast('Archived', 'success');
  };
  const handleRestoreConvSub = (sub) => {
    setConvSubparameters((prev) => prev.map((row) => (row.id === sub.id ? { ...row, status: 'active' } : row)));
    showToast('Restored', 'success');
  };
  const handleArchiveConvParam = (p) => {
    const v = toVariableName(p.display_name);
    const ref = custSubparameters.find((s) => s.status !== 'archived' && !s.system && (s.variables_used || []).includes(v));
    if (ref) return;
    setConvParameters((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'archived' } : x)));
    showToast('Archived', 'success');
  };
  const handleRestoreConvParam = (p) => {
    setConvParameters((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'active' } : x)));
    showToast('Restored', 'success');
  };
  const handleArchiveCustSub = (s) => {
    const v = toVariableName(s.display_name);
    const ref = custParameters.find((p) => p.status !== 'archived' && ((p.subparameters_used || []).includes(v) || (p.variables_used || []).includes(v)));
    if (s?.system || ref) return;
    setCustSubparameters((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: 'archived' } : x)));
    showToast('Archived', 'success');
  };
  const handleRestoreCustSub = (s) => {
    setCustSubparameters((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: 'active' } : x)));
    showToast('Restored', 'success');
  };
  const handleArchiveCustParam = (p) => {
    const v = toVariableName(p.display_name);
    const prevNm = `prev_${v}`;
    const ref = custParameters.find((x) => x.id !== p.id && x.status !== 'archived' && ((x.subparameters_used || []).includes(prevNm) || (x.variables_used || []).includes(prevNm)));
    if (ref) return;
    setCustParameters((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'archived' } : x)));
    showToast('Archived', 'success');
  };
  const handleRestoreCustParam = (p) => {
    setCustParameters((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'active' } : x)));
    showToast('Restored', 'success');
  };

  const resetArchiveModal = () => {
    setShowArchiveModal(false);
    setArchiveItem(null);
    setArchiveType(null);
  };

  const getArchiveBlockers = (type, item) => {
    if (!type || !item) {
      return { systemBlocked: false, sections: [], hasBlockers: false };
    }
    const sections = [];
    const systemBlocked = !!item?.system && (type === 'conv_subparameter' || type === 'cust_subparameter');

    if (type === 'conv_subparameter') {
      const v = toVariableName(item.display_name);
      const usedByConvParams = convParameters.filter((p) => p.status !== 'archived' && (p.subparameters_used || []).includes(v));
      const usedByCustSubparams = custSubparameters.filter((s) => s.status !== 'archived' && !s.system && (s.variables_used || []).includes(v));
      sections.push(
        { title: 'Conversation parameters using this subparameter', items: usedByConvParams.map((p) => ({ id: p.id, label: p.display_name })) },
        { title: 'Customer subparameters using this variable', items: usedByCustSubparams.map((s) => ({ id: s.id, label: s.display_name })) }
      );
    } else if (type === 'conv_parameter') {
      const v = toVariableName(item.display_name);
      const usedByCustSubparams = custSubparameters.filter((s) => s.status !== 'archived' && !s.system && (s.variables_used || []).includes(v));
      sections.push({ title: 'Customer subparameters using this parameter variable', items: usedByCustSubparams.map((s) => ({ id: s.id, label: s.display_name })) });
    } else if (type === 'cust_subparameter') {
      const v = toVariableName(item.display_name);
      const usedByCustParams = custParameters.filter((p) => p.status !== 'archived' && ((p.subparameters_used || []).includes(v) || (p.variables_used || []).includes(v)));
      sections.push({ title: 'Customer parameters using this subparameter', items: usedByCustParams.map((p) => ({ id: p.id, label: p.display_name })) });
    } else if (type === 'cust_parameter') {
      const v = toVariableName(item.display_name);
      const prevNm = `prev_${v}`;
      const referencedByCustParams = custParameters.filter((x) => x.id !== item.id && x.status !== 'archived' && ((x.subparameters_used || []).includes(prevNm) || (x.variables_used || []).includes(prevNm)));
      sections.push({ title: 'Customer parameters referencing this parameter', items: referencedByCustParams.map((p) => ({ id: p.id, label: p.display_name })) });
    }

    const hasDependencyBlockers = sections.some((section) => section.items.length > 0);
    return { systemBlocked, sections, hasBlockers: systemBlocked || hasDependencyBlockers };
  };

  const openArchiveModal = (type, item) => {
    setArchiveType(type);
    setArchiveItem(item);
    setShowArchiveModal(true);
  };

  const confirmArchive = () => {
    if (!archiveType || !archiveItem) return;
    if (archiveType === 'conv_parameter') {
      handleArchiveConvParam(archiveItem);
    } else if (archiveType === 'conv_subparameter') {
      handleArchiveConvSub(archiveItem);
    } else if (archiveType === 'cust_parameter') {
      handleArchiveCustParam(archiveItem);
    } else if (archiveType === 'cust_subparameter') {
      handleArchiveCustSub(archiveItem);
    }
    resetArchiveModal();
  };

  // Import / Export config (for superadmin)
  const handleDownloadConfig = () => {
    try {
      const cfg = buildConfigPayload();
      const blob = new Blob([JSON.stringify(cfg, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `custom_module_config_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Configuration downloaded successfully!', 'success');
    } catch (e) {
      showToast('Error downloading configuration', 'error');
    }
  };
  const handleUploadConfig = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const cfg = JSON.parse(e.target.result);
        setLoading(true);
        const conversationConfig = cfg?.configuration?.conversation || {};
        const customerConfig = cfg?.configuration?.customer || {};
        const boundarySpaceError =
          findBoundarySpaceName(Object.values(conversationConfig.parameters || {}), 'display_name', 'Parameter name') ||
          findBoundarySpaceName(Object.values(customerConfig.parameters || {}), 'display_name', 'Parameter name') ||
          findBoundarySpaceName(Object.values(conversationConfig.subparameters || {}), 'display_name', 'Subparameter name') ||
          findBoundarySpaceName(Object.values(customerConfig.subparameters || {}), 'display_name', 'Subparameter name');
        if (boundarySpaceError) {
          showToast(boundarySpaceError, 'error');
          setLoading(false);
          event.target.value = '';
          return;
        }
        const validationResp = await server.validateModuleJsonConfiguration({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'custom_module', configuration: cfg});
        if (validationResp.status !== 200) {
          showToast(await getApiToastMessage(validationResp, 'Invalid configuration'), 'error');
          setLoading(false);
          event.target.value = '';
          return;
        }
        // Basic validation
        if (!cfg?.configuration?.conversation || !cfg?.configuration?.customer) {
          throw new Error('Invalid configuration format');
        }
        // Transform back to lists
        const conv = cfg.configuration.conversation;
        const cust = cfg.configuration.customer;
        const transformToList = (obj) => {
          const list = [];
          if (!obj) return list;
          const keys = Object.keys(obj);
          keys.forEach((k, idx) => {
            const item = { ...obj[k] };
            item.output_type = normalizeOutputTypeForFrontend(item.output_type);
            list.push({ id: idx + 1, ...item });
          });
          return list;
        };
        setConvSubparameters(transformToList(conv.subparameters));
        setConvParameters(transformToList(conv.parameters));
        setCustSubparameters(transformToList(cust.subparameters));
        setCustParameters(transformToList(cust.parameters));
        setModuleActive(cfg.active === 1 ? true : !!cfg.active);
        setModuleExecution(moduleCallTypeSettings(cfg));
        showToast(await getApiToastMessage(validationResp, 'Configuration uploaded successfully!'), 'success');
        setLoading(false);
      } catch (err) {
        console.error(err);
        showToast('Error uploading configuration: Invalid JSON format', 'error');
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  // Config payload builder
  const buildConfigPayload = () => {
    const toMap = (arr, transformType = 'sub') => {
      return (arr || []).reduce((acc, item) => {
        const key = toVariableName(item.display_name);
        if (transformType === 'sub') {
          const backendType = normalizeOutputTypeForBackend(item.output_type, 'Yes/No');
          acc[key] = {
            output_type: backendType,
            instruction: item.instruction || '',
            ...(!item.logic && transformType === 'sub' ? { context_scope: item.context_scope || 'conversation', ...callTypeSettings(item) } : {}),
            ...classifierSettings(item),
            display_name: item.display_name,
            possible_values: item.possible_values || [],
            system: item.system || 0,
            status: item.status || 'active',
            ...(item.source ? { source: item.source } : {}),
            ...(item.logic ? { logic: item.logic } : {}),
            ...(item.logic_explanation ? { logic_explanation: item.logic_explanation } : {}),
            ...(item.variables_used ? { variables_used: item.variables_used } : {}),
          };
        } else {
          const paramType = normalizeOutputTypeForBackend(item.output_type, 'Number');
          acc[key] = {
            logic: item.logic || '',
            display_name: item.display_name,
            output_type: paramType,
            status: item.status || 'active',
            ...(item.subparameters_used ? { subparameters_used: item.subparameters_used } : {}),
            ...(item.logic_explanation ? { logic_explanation: item.logic_explanation } : {}),
            ...(item.variables_used ? { variables_used: item.variables_used } : {}),
            ...(item.tag ? { tag: item.tag } : {}),
          };
        }
        return acc;
      }, {});
    };
    const configuration = {
      conversation: {
        subparameters: toMap(convSubparameters, 'sub'),
        parameters: toMap(convParameters, 'param')
      },
      customer: {
        subparameters: toMap(custSubparameters, 'sub'),
        parameters: toMap(custParameters, 'param')
      }
    };
    return { configuration, ...moduleExecution, active: moduleActive ? 1 : 0 };
  };

  // Samples/Preview
  const generateSamples = async (sample_flag) => {
    try {
      setLoading(true);
      const config = buildConfigPayload();
      const response = await server.generateSampleModuleOutputs({
        client_id,
        service_id: Array.isArray(service_id) ? service_id[0] : service_id,
        module_name: 'custom_module',
        configuration: config,
        menu: "apps",
        submenu: undefined,
        email,
        'sample_set_flag': sample_flag
      });
      const resp = await response.json();
      if (response.status === 200) {
        const transformed = (resp.data || []).map(sample => {
          const pa = sample.processed_analytics || {};
          const normalizedParams = Array.isArray(pa.parameters)
            ? pa.parameters.map((p) => {
                const output = { ...(p.output || {}) };
                if (Array.isArray(p.subparameters)) {
                  output.subparameters = p.subparameters;
                }
                return { ...p, output };
              })
            : [];
          const normalizedProcessed = { ...pa, parameters: normalizedParams };
          return {
            call_id: sample.call_id,
            employee_name: sample.employee_name,
            call_time: sample.call_time,
            name: sample.name,
            parameters: normalizedProcessed,
            record_link: sample.record_link,
            segments: Array.isArray(sample.segments) ? sample.segments : [],
            record_type: sample.record_type
          };
        });
        setSamples(transformed);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  const deployModule = async (versionDescription) => {
    try {
      setLoading(true);
      let deployed = false;
      let errorMessage = 'Failed to deploy module';
      const config = buildConfigPayload();
      const resp = await server.deployModule({
        client_id,
        service_id: Array.isArray(service_id) ? service_id[0] : service_id,
        email,
        menu: 'apps',
        submenu: undefined,
        module_name: 'custom_module',
        configuration: config,
        default: false,
        'version_changed': current_version,
        version_description: versionDescription
      });
      const saveTags = async () => {
        const savedTags = [...new Set(
          custParameters.filter(p => p.status !== 'archived').map(p => p.tag).filter(Boolean)
        )];
        if (savedTags.length > 0) {
          await server.postBusinessInsightTag({
            client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id,
            email, menu: 'apps', submenu: undefined,
            tag: savedTags, module_name: 'custom_module'
          });
        }
      };
      if (resp.status === 200) {
        await saveTags();
        showToast('Module deployed successfully', 'success');
        deployed = true;
        closeModal();
      } else {
        try {
          const errData = await resp.clone().json();
          errorMessage = errData?.message || errData?.error || errorMessage;
        } catch (_) {}
      }
      if (!deployed) {
        showToast(errorMessage, 'error');
        setLoading(false)
      }
      setLoading(false);
    } catch (e) {
      showToast('Failed to deploy module', 'error');
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      const config = buildConfigPayload();
      const res = await server.postModuleDraftSaveConfiguration({
        client_id,
        service_id,
        email,
        module_name: 'custom_module',
        configuration: config,
        menu: 'apps',
        submenu: undefined
      });
      if (res.status === 200) {
        showToast(await getApiToastMessage(res, 'Draft saved successfully'), 'success');
      } else {
        showToast(await getApiToastMessage(res, 'Failed to save draft'), 'error');
      }
    } catch (e) {
      showToast('Failed to save draft', 'error');
    }
  };

  const fetchTags = async () => {
    try {
      const tagsRes = await server.getBusinessInsightsTags(client_id, service_id, email, 'apps', undefined, 'custom_module');
      if (tagsRes.status === 200) {
        const tagsResp = await tagsRes.json();
        if (tagsResp.data) {
          setAvailableTags(tagsResp.data);
          setTags(tagsResp.data);
        }
      }
    } catch (e) {}
  };

  const getModuleConfig = async () => {
    try {
      const res = await server.getClientModuleConfig(client_id, service_id, email, 'apps', undefined, 'custom_module');
      fetchTags();
      if (res.status === 200) {
        const resp = await res.json();
        const cfg = resp.data?.configuration?.configuration || resp.data?.configuration;
        setModuleDraftSaveConfiguration(resp.data);
        const toList = (obj) => {
          if (!obj) return [];
          const keys = Object.keys(obj);
          return keys.map((k, idx) => {
            const item = { ...obj[k] };
            item.output_type = normalizeOutputTypeForFrontend(item.output_type);
            return { id: idx + 1, ...item };
          });
        };
        setConvSubparameters(toList(cfg?.conversation?.subparameters));
        setConvParameters(toList(cfg?.conversation?.parameters));
        setCustSubparameters(toList(cfg?.customer?.subparameters));
        setCustParameters(toList(cfg?.customer?.parameters));
        setModuleActive(resp.data.active === 1 ? true : false);
        setModuleExecution(moduleCallTypeSettings(resp.data));
        setLoadedModuleExecution(moduleCallTypeSettings(resp.data));
        setCurrent_version(resp.data.version)
      }
    } catch (e) {}
  };

  const getModuleDraftSaveConfiguration = async () => {
    try {
      const res = await server.getModuleDraftSaveConfiguration(client_id, service_id, email, 'apps', undefined, 'custom_module');
      if (res.status === 200) {
        const resp = await res.json();
        if (resp?.data?.configuration) {
          const cfg = resp.data.configuration?.configuration || resp.data.configuration;
          const toList = (obj) => {
            if (!obj) return [];
            const keys = Object.keys(obj);
            return keys.map((k, idx) => {
              const item = { ...obj[k] };
              item.output_type = normalizeOutputTypeForFrontend(item.output_type);
              return { id: idx + 1, ...item };
            });
          };
          setConvSubparameters(toList(cfg?.conversation?.subparameters));
          setConvParameters(toList(cfg?.conversation?.parameters));
          setCustSubparameters(toList(cfg?.customer?.subparameters));
          setCustParameters(toList(cfg?.customer?.parameters));
          setModuleActive(resp.data.active === 1 ? true : false);
          setModuleExecution(moduleCallTypeSettings(resp.data));
          setLoadedModuleExecution(moduleCallTypeSettings(resp.data));
          setModuleDraftSaveConfiguration(resp.data);
        }
      } else {
        showToast('No saved draft.', 'error');
        setConfigType('Live Version');
      }
    } catch (e) {}
  };

  const getDefaultModuleConfigs = async () => {
    try {
      setLoading(true);
      const res = await server.getDefaultModuleConfiguration(client_id, service_id, email, 'apps', undefined, 'custom_module');
      if (res.status === 200) {
        const resp = await res.json();
        if (resp.data) {
          setDefaultConfig(resp.data);
        }
      }
      setLoading(false);
    } catch (e) { setLoading(false); }
  };

  const resetToDefault = () => {
    try {
      if (!defaultConfig) {
        showToast('No default configuration found', 'error');
        return;
      }
      const cfg = defaultConfig.configuration || { conversation: { subparameters: {}, parameters: {} }, customer: { subparameters: {}, parameters: {} } };
      const toList = (obj) => {
        if (!obj) return [];
        const keys = Object.keys(obj);
        return keys.map((k, idx) => {
          const item = { ...obj[k] };
          item.output_type = normalizeOutputTypeForFrontend(item.output_type);
          return { id: idx + 1, ...item };
        });
      };
      // If there are presets, pick default_preset; else use root
      const presetKey = defaultConfig.default_preset;
      const useCfg = presetKey && cfg[presetKey] ? cfg[presetKey] : cfg;
      setConvSubparameters(toList(useCfg?.conversation?.subparameters));
      setConvParameters(toList(useCfg?.conversation?.parameters));
      setCustSubparameters(toList(useCfg?.customer?.subparameters));
      setCustParameters(toList(useCfg?.customer?.parameters));
      setModuleActive(true);
      setModuleExecution(moduleCallTypeSettings(defaultConfig || {}));
      setLoadedModuleExecution(moduleCallTypeSettings(defaultConfig || {}));
      showToast('Loaded default configuration', 'success');
    } catch (_) {}
  };

  const getProjectStatus = async () => {
    try {
      let res = await server.getProjectStatus(client_id, service_id, email, 'apps', undefined, window.location.href);
      if (res.status === 200) {
        let resp = await res.json();
        if (resp.data && resp.data === 'completed') setShowSampleTest(true);
      }
    } catch (e) {}
  };
  const handleCloseSampleTestModal = () => {
    setShowSampleTestModal(false);
  };

  // Audio/Chat helpers copied from AF/Lead
  const convertToSeconds = (time) => {
    if (typeof time === 'string') {
      const [hours, minutes, seconds] = time.split(':').map(Number);
      return (hours * 3600) + (minutes * 60) + seconds;
    }
    return time;
  };
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  function formatEpochTime(epochMillis) {
    if (typeof epochMillis !== 'number' || epochMillis.toString().length !== 13) return "";
    const date = new Date(epochMillis);
    return date.toLocaleString();
  }
  const scrollToSegment = (idx) => {
    const el = segmentRefs.current[idx];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const toggleSegment = (item, idx, link) => {
    setSelectedSegmentIdx(idx);
    scrollToSegment(idx);
    if (!link) return;
    if (audioRef.current) {
      audioRef.current.currentTime = item.start_time;
      audioRef.current.play();
    }
  };
  const handleAudioTimeUpdate = (segments) => {
    if (sourceClickInProgressRef.current) return;
    if (!audioRef.current) return;
    const currentTime = audioRef.current.currentTime;
    segments.forEach((segment, idx) => {
      if (currentTime >= segment.start_time && currentTime <= segment.end_time) {
        if (segmentRefs.current[idx]) {
          segmentRefs.current[idx].scrollIntoView({ behavior: "smooth", block: "nearest" });
          setSelectedSegmentIdx(idx);
        }
      }
    });
  };
  const getBorderColor = (actor_type) => {
    switch (actor_type) {
      case 'user': return "4px solid #6f6ffe";
      case 'agent': return "4px solid #3B82F6";
      case 'bot': return "4px solid gray";
      case 'system': return "4px solid gray";
      default: break;
    }
  };
  const getBackgroundColor = (actor_type) => {
    switch (actor_type) {
      case 'user': return "rgb(249, 250, 251)";
      case 'agent': return "rgb(249, 250, 251)";
      case 'bot': return "rgb(249, 250, 251)";
      case 'system': return "rgb(249, 250, 251)";
      default: break;
    }
  };
  const handleSourceClick = (source, segments) => {
    let start, end, message_id;
    if (source.start_timestamp && source.end_timestamp) {
      sourceClickInProgressRef.current = true;
      try {
        start = typeof source.start_timestamp === 'string' ? convertToSeconds(source.start_timestamp) - 1 : parseFloat(source.start_timestamp) - 1;
        end = typeof source.end_timestamp === 'string' ? convertToSeconds(source.end_timestamp) + 1 : parseFloat(source.end_timestamp) + 1;
        if (isNaN(start) || isNaN(end) || start >= end) {
          sourceClickInProgressRef.current = false;
          return;
        }
      } catch (e) {
        sourceClickInProgressRef.current = false;
        return;
      }
      if (!audioRef.current) {
        sourceClickInProgressRef.current = false;
        return;
      }
      try {
        audioRef.current.pause();
        if (audioRef.current._currentTimeUpdateHandler) {
          audioRef.current.removeEventListener('timeupdate', audioRef.current._currentTimeUpdateHandler);
          audioRef.current._currentTimeUpdateHandler = null;
        }
        const timeUpdateHandler = () => {
          if (!audioRef.current) return;
          const currentTime = audioRef.current.currentTime;
          if (currentTime >= end) {
            audioRef.current.pause();
            audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
            audioRef.current._currentTimeUpdateHandler = null;
          }
        };
        audioRef.current._currentTimeUpdateHandler = timeUpdateHandler;
        audioRef.current.addEventListener('timeupdate', timeUpdateHandler);
        audioRef.current.currentTime = start;
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            let idx = 0;
            for (let i = 0; i < segments.length; i++) {
              if (start >= segments[i].start_time && start <= segments[i].end_time) { idx = i; break; }
            }
            setSelectedSegmentIdx(idx);
            if (segmentRefs.current[idx] && idx === selectedSegmentIdx) {
              segmentRefs.current[idx].scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
            sourceClickInProgressRef.current = false;
          }).catch(() => {
            audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
            audioRef.current._currentTimeUpdateHandler = null;
            sourceClickInProgressRef.current = false;
          });
        }
      } catch (e) {
        sourceClickInProgressRef.current = false;
      }
    } else {
      message_id = source.segment_id;
      if (message_id) {
        const messageIndex = segments.findIndex(msg => msg.message_id === message_id);
        if (messageIndex !== -1) {
          if (!segmentRefs.current[messageIndex]) segmentRefs.current[messageIndex] = React.createRef();
          setTimeout(() => {
            const messageElement = document.querySelector(`[data-message-id="${message_id}"]`);
            if (messageElement) {
              messageElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
              messageElement.style.backgroundColor = "rgba(111, 111, 254, 0.1)";
              setTimeout(() => { messageElement.style.backgroundColor = ""; }, 2000);
            }
          }, 100);
        }
      }
    }
  };

  // Initial wiring
  useEffect(() => {
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
    if (user && clientID) {
      setEmail(user.email);
      setClient_id(clientID);
    }
    if (serviceID) setService_id(serviceID);
  }, [clientID, serviceID]);

  useEffect(() => {
    if (client_id && service_id && email) {
      getModuleConfig();
      getDefaultModuleConfigs();
      getProjectStatus();
      // Initial preview
      generateSamples(1);
    }
  }, [client_id, service_id, email]);

  useEffect(() => {
    if (!client_id || !service_id || !email) return;
    if (configType === 'Saved Draft') {
      getModuleDraftSaveConfiguration();
    } else if (configType === 'Default') {
      resetToDefault();
    } else {
      getModuleConfig();
    }
  }, [configType]);

  // Update parent with config while editing (for parity with other modules)
  useEffect(() => {
    if (moduleDraftSaveConfiguration) {
      const cfg = buildConfigPayload();
      if (typeof config_data === 'function') config_data(cfg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleDraftSaveConfiguration, moduleActive, moduleExecution, convSubparameters, convParameters, custSubparameters, custParameters]);

  const getLoadedConfigForDiff = () => (
    configType === 'Default'
      ? (
        (defaultConfig?.default_preset && defaultConfig?.configuration?.[defaultConfig.default_preset]) ||
        defaultConfig?.configuration ||
        {}
      )
      : (
        moduleDraftSaveConfiguration?.configuration?.configuration ||
        moduleDraftSaveConfiguration?.configuration ||
        {}
      )
  );

  const getCurrentConfigForDiff = () => buildConfigPayload().configuration;

  // Render
  if (!showPreview) {
    return (
      <div className="p-3 h-full min-h-0 overflow-hidden">
        <div className="mx-auto flex h-full min-h-0 flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 shrink-0">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">Custom Module <span className='inline-flex items-center px-2 py-1 rounded-full whitespace-nowrap font-normal text-xs bg-gray-200 ml-1' style={{color: "gray"}}>{configType}</span></h1>
              <p className="text-gray-500 text-sm mt-1">Configure conversation and customer sides</p>
              <div className="flex items-center gap-2 mt-3">
                <label className="cursor-pointer">
                  <input type="file" accept=".json" onChange={handleUploadConfig} className="hidden" id="custom-config-upload" />
                  <button type="button" className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg border border-blue-300 hover:bg-blue-200 flex items-center text-sm" onClick={() => document.getElementById('custom-config-upload').click()}>
                    <Upload className="w-4 h-4 mr-1" /> Upload Config
                  </button>
                </label>
                <button type="button" className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg border border-green-300 hover:bg-green-200 flex items-center text-sm" onClick={handleDownloadConfig}>
                  <Download className="w-4 h-4 mr-1" /> Download Config
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-y-2">
              <div className="relative inline-block w-12 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  checked={moduleActive}
                  onChange={() => setModuleActive(prev => !prev)}
                  className="sr-only"
                  id="toggle-module-active"
                />
                <label
                  htmlFor="toggle-module-active"
                  className={`block h-5 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${moduleActive ? 'bg-indigo-600' : 'bg-gray-200'}`}
                >
                  <span className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${moduleActive ? 'translate-x-7' : 'translate-x-0'}`} />
                </label>
              </div>
              <button
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 mr-2 flex items-center"
                onClick={handleSaveDraft}
              >
                <Save className="w-4 h-4 mr-2" /> Save as Draft
              </button>
              <ConfigurationDiffButton
                title="Custom Module Changes"
                originalConfig={() => configWithExecutionSettings(getLoadedConfigForDiff(), loadedModuleExecution)}
                modifiedConfig={() => configWithExecutionSettings(getCurrentConfigForDiff(), moduleExecution)}
              />
              {showSampleTest && (
                <button
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                  onClick={() => setShowSampleTestModal(true)}
                >
                  Manage Test Set
                </button>
              )}
              <div className="relative inline-block mx-2">
                <button
                  className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                  onClick={() => setShowConfigDropdown((prev) => !prev)} style={{width: "10rem", justifyContent: "space-between"}}
                  type="button"
                >
                  {configType}
                  <span className="ml-2">
                    {showConfigDropdown ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </span>
                </button>
                {showConfigDropdown && (
                  <div className="absolute z-10 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg">
                    {['Live Version','Saved Draft','Default'].map(option => (
                      <div
                        key={option}
                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm ${configType === option ? 'text-indigo-600' : ''}`}
                        onClick={() => { setConfigType(option); setShowConfigDropdown(false); }}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                onClick={() => { setShowPreview(true); setSamples([]); setExpandedSample(null); generateSamples(1); }}
                style={{height: "40px", borderRadius: "4px"}}
              >
                Preview
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 shrink-0">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              Also run analytics for
              <span className="relative group">
                <button type="button" aria-label="About call-type settings" className="flex text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded">
                  <Info size={16} />
                </button>
                <span role="tooltip" className="absolute left-0 top-full z-50 mt-2 hidden w-64 rounded bg-gray-800 px-3 py-2 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block">
                  Proper conversations run by default when the module is active. For other conversation types, enable the module option and each required parameter separately.
                </span>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {callTypeOptions.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500"
                    checked={moduleExecution[key] === 1}
                    onChange={e => setModuleExecution(prev => ({...prev, [key]: e.target.checked ? 1 : 0}))} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {!loading && <div className="bg-white rounded-lg shadow min-h-0 flex-1 overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <nav className="flex overflow-x-auto whitespace-nowrap" aria-label="Tabs">
                <button
                  onClick={() => setSideTab('conversation')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${sideTab === 'conversation' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  Conversation Value
                </button>
                <button
                  onClick={() => setSideTab('customer')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${sideTab === 'customer' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  Customer Value
                </button>
              </nav>
            </div>

            {sideTab === 'conversation' && (
              <div className="p-4">
                <div className="flex border-b border-gray-200 mb-4" aria-label="Conversation sections">
                  {['parameters', 'subparameters'].map(tab => (
                    <button
                      key={tab}
                      type="button"
                      aria-pressed={convInnerTab === tab}
                      onClick={() => setConvInnerTab(tab)}
                      className={`py-3 px-6 border-b-2 font-medium text-sm ${convInnerTab === tab ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                      {tab === 'parameters' ? 'Parameters' : 'Subparameters'}
                    </button>
                  ))}
                </div>
                <div hidden={convInnerTab !== 'parameters'}>
                {/* Parameters Section */}
                <div className='p-6 border-b border-gray-200 shadow-md rounded-lg'>
                  <h2 className="text-lg font-medium text-gray-800 mb-3">Parameters</h2>
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex-1 min-w-[12rem]"><ConfigurationTableSearch className="" label="Search conversation parameters by name" value={tableSearch.conversationParameters} onChange={value => setTableSearch({ ...tableSearch, conversationParameters: value })} /></div>
                    <div className="flex flex-wrap items-center gap-4 shrink-0">
                      <label className="flex items-center whitespace-nowrap text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={showArchivedConvParameters}
                          onChange={(e) => setShowArchivedConvParameters(e.target.checked)}
                          className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Show archived parameters
                      </label>
                      <button className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}} onClick={openAddConvParam}>Add Parameter</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="configuration-table divide-y divide-gray-200">
                      <colgroup>
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "28%" }} />
                        <col />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "160px" }} />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logic</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subparameters Used</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Output Type</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parameterTableRows(filterTableRows(convParameters, showArchivedConvParameters), tableSearch.conversationParameters).map(p => (
                          <tr key={p.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-wrap">{p.display_name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 text-wrap">
                              <code className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">{(p.logic || '').length > 300 ? (p.logic || '').slice(0, 300) + '...' : (p.logic || '')}</code>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 text-wrap">{(p.subparameters_used || []).join(', ')}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{p.output_type || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {p.status === 'archived' ? (
                                <button type="button" className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" title="Restore" onClick={() => handleRestoreConvParam(p)}>
                                  <TbRestore className="h-4 w-4" />
                                </button>
                              ) : (
                                <div className="inline-flex items-center justify-end gap-3">
                                  <button type="button" className="text-indigo-600 hover:text-indigo-900" onClick={() => openEditConvParam(p.id)}>
                                    <FaRegEdit/>
                                  </button>
                                  <button type="button" className="text-red-600 hover:text-red-900" title="Archive" onClick={() => openArchiveModal('conv_parameter', p)}>
                                    <Archive className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {showConvParamModal && (
                  <div ref={convParamFormRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4" style={{width: "85%", margin: "1rem auto"}}>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-medium text-gray-800">{isEditConvParam ? 'Edit Conversation Parameter' : 'Add Conversation Parameter'}</h3>
                      <button onClick={() => setShowConvParamModal(false)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{color: "slategray"}}/></button>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Parameter Name</label>
                      <input className="w-full px-4 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" type="text" placeholder="Enter Parameter Name" value={convParamName} disabled={isEditConvParam} onChange={(e) => setConvParamName(e.target.value)} />
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subparameters Used</label>
                      <div className="flex gap-2 mb-2 relative">
                        <input
                          ref={convParamInputRef}
                          type="text"
                          value={convParamSelectedSubparam}
                          onChange={(e) => { setConvParamSelectedSubparam(e.target.value); setConvParamShowDropdown(true); }}
                          onFocus={() => setConvParamShowDropdown(true)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addConvSubparamToQuery(); } }}
                          className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                          placeholder="Enter subparameter name"
                        />
                        <button
                          onClick={() => addConvSubparamToQuery()}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                          Add
                        </button>
                        {convParamShowDropdown && filteredConvSubVars.length > 0 && (
                          <div
                            ref={convParamDropdownRef}
                            className="absolute top-full left-0 right-24 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto"
                          >
                            {filteredConvSubVars.map((name) => (
                              <div
                                key={name}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => { setConvParamSelectedSubparam(name); setConvParamShowDropdown(false); }}
                              >
                                {name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {convParamSelectedSubs.map((name) => (
                          <div key={name} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                            <span className="text-sm text-gray-700">{name}</span>
                            <button onClick={() => removeConvSubparamFromQuery(name)} className="text-gray-500 hover:text-gray-700">
                              <FaTimes style={{color: "slategray"}}/>
                            </button>
                          </div>
                        ))}
                      </div>
                      {/* Available variables UI removed as per requirement */}
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <label className="block text-sm font-medium text-gray-700">Logic</label>
                        <div className="relative">
                          <Info size={16} className="text-gray-400" />
                        </div>
                      </div>
                      <button onClick={() => verifyConvParamQuery()} className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300" style={{height: "40px", borderRadius: "4px"}}>Validate Logic</button>
                    </div>
                    <div className='p-3 border rounded-lg my-2 mb-4 bg-indigo-50 border-indigo-200'>
                      <div className='flex items-center justify-between h-6 cursor-pointer' onClick={() => setWriteWithAIConvParam(!writeWithAIConvParam)}>
                        <div className="text-sm font-medium flex items-center text-indigo-600">
                          <Wand2 className='h-4 w-4 mr-2 text-indigo-600'/> Write with AI
                        </div>
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={writeWithAIConvParam ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                        </svg>
                      </div>
                      {writeWithAIConvParam && (
                        <div className='mt-2'>
                          <textarea className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" rows="5" placeholder="Describe the logic you want to implement using the conversation subparameters…" onChange={(e) => {}} />
                          <div className='flex justify-end mt-2'>
                            <button
                              onClick={() => generateLogicWithAI('', setConvParamManualQuery, (q) => verifyConvParamQuery(q, allowedConvSubVars), convSubparameters.filter((s) => s.status !== 'archived'))}
                              className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center text-sm" style={{height: "35px", borderRadius: "4px"}}
                            >
                              <Wand2 className='h-3 w-3 mr-2'/> Generate
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {!logicLoading && (
                      <div className="mb-6">
                        <div className='flex items-center gap-2 mt-2'>
                <p style={{fontSize: "12px", fontStyle: "italic", color: "gray", width: "60%"}}>Score can be a number or text</p>
                          <p style={{fontSize: "12px", fontStyle: "italic", color: "gray", width: "40%"}}>Explanation</p>
                        </div>
                        <div className='flex items-start gap-2'>
                          <textarea className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" rows="6" placeholder="Enter your logic here..." value={convParamManualQuery} onChange={(e) => setConvParamManualQuery(e.target.value)} style={{border: convParamQueryError ? "1px solid red" : "", width: "60%", height: "200px", overflow: "auto", fontSize: "14px"}} />
                          <textarea className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" value={convParamLogicExplanation} rows="7" onChange={(e) => setConvParamLogicExplanation(e.target.value)} style={{width: "40%", fontSize: "14px", height: "200px", overflow: "auto", whiteSpace: "pre-line"}} />
                        </div>
                        {convParamQueryError && (<p className="mt-2 text-sm text-red-600">{convParamQueryError}</p>)}
            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
                <ModernTimeDropdown
                  width='w-full'
                  placeholder="Select Output Type"
                  value={convParamOutputType === 'Date and Time' ? 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)' : convParamOutputType}
                  options={['Yes/No/NA', 'Categorical - Single Value', 'Categorical - Multiple Values', 'Number', 'Text', 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)']}
                  isDisabled={isEditConvParam}
                  onChange={(e) => {
                    const label = 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)';
                    setConvParamOutputType(e.target.value === label ? 'Date and Time' : e.target.value);
                  }}
                />
              </div>
                      </div>
                    )}
                    {logicLoading && (
                      <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                        <div className="loader"></div>
                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                      </div>
                    )}
                    <div className="flex justify-end gap-3">
                      <button onClick={() => setShowConvParamModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}>Cancel</button>
                      <button onClick={saveConvParameter} className={`px-4 py-2 text-white rounded-lg ${(!convParamManualQuery || convParamQueryError) ? 'bg-gray-500 hover:bg-gray-700' : 'bg-indigo-600 hover:bg-indigo-700'}`} style={{height: "40px", borderRadius: "4px"}}>Save</button>
                    </div>
                  </div>
                )}

                </div>
                <div hidden={convInnerTab !== 'subparameters'}>
                {/* Subparameters Section */}
                <div className='p-6 border-b border-gray-200 shadow-md rounded-lg'>
                    <h2 className="text-lg font-medium text-gray-800 mb-3">Subparameters</h2>
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex-1 min-w-[12rem]"><ConfigurationTableSearch className="" label="Search conversation subparameters by name" value={tableSearch.conversationSubparameters} onChange={value => setTableSearch({ ...tableSearch, conversationSubparameters: value })} /></div>
                    <div className="flex flex-wrap items-center gap-4 shrink-0">
                      <label className="flex items-center whitespace-nowrap text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={showArchivedConvSubparameters}
                          onChange={(e) => setShowArchivedConvSubparameters(e.target.checked)}
                          className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Show archived subparameters
                      </label>
                      <button className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}} onClick={openAddConvSubparam}>Add Subparameter</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="configuration-table divide-y divide-gray-200">
                      <colgroup>
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "85px" }} />
                        <col style={{ width: "14%" }} />
                        <col />
                        <col style={{ width: "90px" }} />
                        <col style={{ width: "160px" }} />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Definition / Logic</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kind</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Options</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parameterTableRows(filterTableRows(convSubparameters, showArchivedConvSubparameters), tableSearch.conversationSubparameters).map(sp => (
                          <tr key={sp.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-wrap">{sp.display_name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="truncate" title={sp.logic || sp.instruction || ''}>
                                {sp.logic ? <code className="bg-gray-100 px-2 py-1 rounded">{sp.logic.length > 100 ? sp.logic.slice(0, 100) + '...' : sp.logic}</code> : sp.instruction || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {sp.system === 2 ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Client</span> :
                               sp.system ? <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">System</span> :
                               sp.logic || sp.subparameter_type === 'logic_based' ? <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">Logic</span> :
                               sp.subparameter_type === 'classifier' ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Classifier</span> :
                               <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">LLM</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sp.output_type || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-wrap">{sp.output_type !== 'Number' ? <ConfigurationOptions options={sp.possible_values} /> : '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className={`px-2 py-1 rounded-full text-xs ${!sp.status || sp.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {sp.status === 'archived' ? 'Archived' : sp.status === 'inactive' ? 'Inactive' : 'Active'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {sp.system ? (
                                <span className="text-gray-400">System Parameter</span>
                              ) : sp.status === 'archived' ? (
                                <button type="button" className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" title="Restore" onClick={() => handleRestoreConvSub(sp)}>
                                  <TbRestore className="h-4 w-4" />
                                </button>
                              ) : (
                                <div className="inline-flex items-center justify-end gap-3">
                                  <button type="button" className="text-indigo-600 hover:text-indigo-900 text-md" onClick={() => openEditConvSubparam(sp.id)}>
                                    <FaRegEdit/>
                                  </button>
                                  <button type="button" className="text-red-600 hover:text-red-900" title="Archive" onClick={() => openArchiveModal('conv_subparameter', sp)}>
                                    <Archive className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {showConvSubparamModal && (
                  <div ref={convSubparamFormRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4" style={{width: "85%", margin: "1rem auto"}}>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-medium text-gray-800">{editingConvSubparam ? 'Edit Conversation Subparameter' : 'Add Conversation Subparameter'}</h3>
                      <button onClick={() => setShowConvSubparamModal(false)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{color: "slategray"}}/></button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                        <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md global-input" value={convSubparamName} onChange={(e) => setConvSubparamName(e.target.value)} disabled={!!editingConvSubparam?.id} />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Context
                        </label>
                        <ModernTimeDropdown
                          width="w-full"
                          ariaLabel="Context"
                          value={contextScope === 'customer' ? 'Customer context' : 'Conversation context (default)'}
                          options={['Conversation context (default)', 'Customer context']}
                          onChange={(e) => setContextScope(e.target.value === 'Customer context' ? 'customer' : 'conversation')}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {contextScope === 'customer'
                            ? 'Uses up to 50 recent conversations, including this call and non-connected calls.'
                            : 'Uses this conversation only.'}
                        </p>
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Run this parameter for</p>
                          <div className="flex flex-wrap gap-4">
                            {callTypeOptions.map(([key, label]) => (
                              <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                                <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500"
                                  disabled={moduleExecution[key] !== 1}
                                  checked={moduleExecution[key] === 1 && parameterExecution[key] === 1}
                                  onChange={e => setParameterExecution(prev => ({...prev, [key]: e.target.checked ? 1 : 0}))} />
                                {label}
                              </label>
                            ))}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">Enable the module call type, then opt this parameter in. Disabled parameters return None; existing calculation rules still apply.</p>
                        </div>

                      </div>
                      <div className="col-span-2"><ClassifierFields value={{ ...classification, output_type: convSubparamType, possible_values: convSubparamPossibleValues, instruction: convSubparamDefinition }} onChange={setClassification} /></div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Definition</label>
                        <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md global-input" rows="3" value={convSubparamDefinition} onChange={(e) => setConvSubparamDefinition(e.target.value)} />
                      </div>
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                        <ModernTimeDropdown
                          width='w-full'
                          placeholder="Select Type"
                          value={convSubparamType === 'Date and Time' ? 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)' : convSubparamType}
                          options={classifierOutputOptions(classification.subparameter_type, ['Yes/No/NA', 'Categorical - Single Value', 'Categorical - Multiple Values', 'Number', 'Text', 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)'])}
                          isDisabled={!!editingConvSubparam?.id}
                          onChange={(e) => {
                            const label = 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)';
                            setConvSubparamType(e.target.value === label ? 'Date and Time' : e.target.value);
                          }}
                        />
                      </div>
                      {convSubparamType.startsWith('Categorical') && (
                        <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                          <div className="flex gap-2 mb-2">
                            <input type="text" className="flex-1 px-3 py-2 border border-gray-300 rounded-md global-input" value={convSubparamCurrentTag} onChange={(e) => setConvSubparamCurrentTag(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addConvSubparamTag()} placeholder="Type and press Enter to add" />
                            <button onClick={addConvSubparamTag} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Add</button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {convSubparamPossibleValues.map((tag, i) => (
                              <div key={i} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                                <span className="text-sm text-gray-700">{tag}</span>
                                <button onClick={() => removeConvSubparamTag(tag)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{color: "slategray"}}/></button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {convSubSaveLoading && (
                      <div className="flex justify-center items-center my-4" style={{ flexDirection: "column" }}>
                        <div className="loader"></div>
                        <div style={{textAlign: "center"}}>Saving <br/> Please Wait</div>
                      </div>
                    )}
                    <div className="flex justify-end gap-3 mt-4">
                      <button onClick={() => setShowConvSubparamModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}>Cancel</button>
                      <button onClick={saveConvSubparam} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}}>Save</button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            )}

            {sideTab === 'customer' && (
              <div className="p-4">
                <div className="flex border-b border-gray-200 mb-4" aria-label="Customer sections">
                  {['parameters', 'subparameters'].map(tab => (
                    <button
                      key={tab}
                      type="button"
                      aria-pressed={custInnerTab === tab}
                      onClick={() => setCustInnerTab(tab)}
                      className={`py-3 px-6 border-b-2 font-medium text-sm ${custInnerTab === tab ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                      {tab === 'parameters' ? 'Parameters' : 'Subparameters'}
                    </button>
                  ))}
                </div>
                <div hidden={custInnerTab !== 'parameters'}>
                {/* Parameters Section */}
                <div className='p-6 border-b border-gray-200 shadow-md rounded-lg'>
                  <h2 className="text-lg font-medium text-gray-800 mb-3">Parameters</h2>
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex-1 min-w-[12rem]"><ConfigurationTableSearch className="" label="Search customer parameters by name" value={tableSearch.customerParameters} onChange={value => setTableSearch({ ...tableSearch, customerParameters: value })} /></div>
                    <div className="flex flex-wrap items-center gap-4 shrink-0">
                      <label className="flex items-center whitespace-nowrap text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={showArchivedCustParameters}
                          onChange={(e) => setShowArchivedCustParameters(e.target.checked)}
                          className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Show archived parameters
                      </label>
                      <button className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}} onClick={openAddCustParam}>Add Parameter</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="configuration-table divide-y divide-gray-200">
                      <colgroup>
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "28%" }} />
                        <col />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "160px" }} />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logic</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subparameters Used</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Output Type</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parameterTableRows(filterTableRows(custParameters, showArchivedCustParameters), tableSearch.customerParameters).map(p => (
                          <tr key={p.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{p.display_name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                              <code className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">{(p.logic || '').length > 300 ? (p.logic || '').slice(0, 300) + '...' : (p.logic || '')}</code>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 text-wrap">{Array.isArray(p.subparameters_used) ? p.subparameters_used.join(', ') : (Array.isArray(p.variables_used) ? p.variables_used.join(', ') : '-')}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{p.output_type || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {p.status === 'archived' ? (
                                <button type="button" className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" title="Restore" onClick={() => handleRestoreCustParam(p)}>
                                  <TbRestore className="h-4 w-4" />
                                </button>
                              ) : (
                                <div className="inline-flex items-center justify-end gap-3">
                                  <button type="button" className="text-indigo-600 hover:text-indigo-900" onClick={() => openEditCustParam(p.id)}>
                                    <FaRegEdit/>
                                  </button>
                                  <button type="button" className="text-red-600 hover:text-red-900" title="Archive" onClick={() => openArchiveModal('cust_parameter', p)}>
                                    <Archive className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {showCustParamModal && (
                  <div ref={custParamFormRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4" style={{width: "85%", margin: "1rem auto"}}>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-medium text-gray-800">{isEditCustParam ? 'Edit Customer Parameter' : 'Add Customer Parameter'}</h3>
                      <button onClick={() => setShowCustParamModal(false)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{color: "slategray"}}/></button>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Parameter Name</label>
                      <input className="w-full px-4 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" type="text" placeholder="Enter Parameter Name" value={custParamName} disabled={isEditCustParam} onChange={(e) => setCustParamName(e.target.value)} />
                    </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subparameters Used</label>
                  <div className="flex gap-2 mb-2 relative">
                    <input
                      ref={custParamInputRef}
                      type="text"
                      value={custParamSelectedVar}
                      onChange={(e) => { setCustParamSelectedVar(e.target.value); setCustParamShowDropdown(true); }}
                      onFocus={() => setCustParamShowDropdown(true)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustVarToParam(); } }}
                      className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                      placeholder="Enter variable name"
                    />
                    <button
                      onClick={() => addCustVarToParam()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      Add
                    </button>
                    {custParamShowDropdown && filteredCustParamVars.length > 0 && (
                      <div
                        ref={custParamDropdownRef}
                        className="absolute top-full left-0 right-24 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto"
                      >
                        {filteredCustParamVars.map((name) => (
                          <div
                            key={name}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => { setCustParamSelectedVar(name); setCustParamShowDropdown(false); }}
                          >
                            {name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(() => {
                    const currentSubs = (custParamSelectedVars || []).filter(n => custSubVars.includes(n));
                    const prevParams = (custParamSelectedVars || []).filter(n => n.startsWith('prev_') && custParamVars.includes(n.slice(5)));
                    return (
                      <>
                        {currentSubs.length > 0 && (
                          <>
                            <div className="text-xs font-medium text-gray-700 mb-1">Current customer subparameters:</div>
                            <div className="flex flex-wrap gap-2">
                              {currentSubs.map((name) => (
                                <div key={`cur_${name}`} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                                  <span className="text-sm text-gray-700">{name}</span>
                                  <button onClick={() => removeCustVarFromParam(name)} className="text-gray-500 hover:text-gray-700">
                                    <FaTimes style={{color: "slategray"}}/>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        {prevParams.length > 0 && (
                          <>
                            <div className="text-xs font-medium text-gray-700 mt-2 mb-1">Previous customer parameters:</div>
                            <div className="flex flex-wrap gap-2">
                              {prevParams.map((name) => (
                                <div key={`prev_${name}`} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                                  <span className="text-sm text-gray-700">{name}</span>
                                  <button onClick={() => removeCustVarFromParam(name)} className="text-gray-500 hover:text-gray-700">
                                    <FaTimes style={{color: "slategray"}}/>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <label className="block text-sm font-medium text-gray-700">Logic</label>
                        <div className="relative">
                          <Info size={16} className="text-gray-400" />
                        </div>
                      </div>
                      <button onClick={() => verifyCustParamQuery()} className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300" style={{height: "40px", borderRadius: "4px"}}>Validate Logic</button>
                    </div>
                    {!logicLoading && (
                      <div className="mb-6">
                        <div className='flex items-center gap-2 mt-2'>
                <p style={{fontSize: "12px", fontStyle: "italic", color: "gray", width: "60%"}}>Score can be a number or text</p>
                          <p style={{fontSize: "12px", fontStyle: "italic", color: "gray", width: "40%"}}>Explanation</p>
                        </div>
                        <div className='flex items-start gap-2'>
                          <textarea className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" rows="6" placeholder="Enter your logic here..." value={custParamManualQuery} onChange={(e) => setCustParamManualQuery(e.target.value)} style={{border: custParamQueryError ? "1px solid red" : "", width: "60%", height: "200px", overflow: "auto", fontSize: "14px"}} />
                          <textarea className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" value={custParamLogicExplanation} onChange={(e) => setCustParamLogicExplanation(e.target.value)} rows="6" style={{ width: "40%", height: "200px", overflow: "auto", fontSize: "14px", whiteSpace: "pre-line"}} />
                        </div>
              {/* {Array.isArray(custParamVariablesUsed) && custParamVariablesUsed.length > 0 && (
                <div className="text-xs text-gray-500 mt-1">Subparameters Used: {custParamVariablesUsed.join(', ')}</div>
              )} */}
                        {custParamQueryError && (<p className="mt-2 text-sm text-red-600">{custParamQueryError}</p>)}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
                          <ModernTimeDropdown
                            width='w-full'
                            placeholder="Select Output Type"
                            value={custParamOutputType === 'Date and Time' ? 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)' : custParamOutputType}
                            options={['Yes/No/NA', 'Categorical - Single Value', 'Categorical - Multiple Values', 'Number', 'Text', 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)']}
                            isDisabled={isEditCustParam}
                            onChange={(e) => {
                              const label = 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)';
                              setCustParamOutputType(e.target.value === label ? 'Date and Time' : e.target.value);
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {logicLoading && (
                      <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                        <div className="loader"></div>
                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                      </div>
                    )}
                    {/* Tag Selector */}
                    <div className="mb-6 relative">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Parameter Tag</label>
                      <div className="relative">
                        <div
                          className="border border-gray-300 rounded-md p-2 flex justify-between items-center cursor-pointer bg-white"
                          onClick={() => setIsTagOpen(!isTagOpen)}
                        >
                          <span className={custParamTag ? "text-gray-900" : "text-gray-400"}>
                            {custParamTag || "Select a tag"}
                          </span>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isTagOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                          </svg>
                        </div>
                        {isTagOpen && (
                          <div className="absolute mt-1 w-full rounded-md shadow-lg bg-white z-10 border border-gray-200">
                            <ul className="max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                              {tags.map((tag) => (
                                <li
                                  key={tag}
                                  className="text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9 hover:bg-blue-50"
                                  onClick={() => { setCustParamTag(tag); setIsTagOpen(false); }}
                                >
                                  {tag}
                                </li>
                              ))}
                              {!isAddingNewTag ? (
                                <li
                                  className="text-blue-600 cursor-pointer select-none relative py-2 pl-3 pr-9 border-t border-gray-200 hover:bg-blue-50"
                                  onClick={() => setIsAddingNewTag(true)}
                                >
                                  + Add new tag
                                </li>
                              ) : (
                                <li className="border-t border-gray-200 p-2">
                                  <div className="flex flex-wrap items-center gap-y-2">
                                    <input
                                      type="text"
                                      className="flex-grow border border-gray-300 rounded-md p-1 text-sm focus:outline-none global-input"
                                      placeholder="Enter new tag"
                                      value={newTagValue}
                                      onChange={(e) => setNewTagValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          if (newTagValue.trim()) {
                                            setTags(prev => [...prev, newTagValue.trim()]);
                                            setCustParamTag(newTagValue.trim());
                                            setNewTagValue('');
                                            setIsAddingNewTag(false);
                                            setIsTagOpen(false);
                                          }
                                        }
                                      }}
                                      autoFocus
                                    />
                                    <button
                                      className="ml-2 bg-blue-600 text-white px-2 py-1 rounded-md text-sm"
                                      onClick={() => {
                                        if (newTagValue.trim()) {
                                          setTags(prev => [...prev, newTagValue.trim()]);
                                          setCustParamTag(newTagValue.trim());
                                          setNewTagValue('');
                                          setIsAddingNewTag(false);
                                          setIsTagOpen(false);
                                        }
                                      }}
                                    >
                                      Add
                                    </button>
                                    <button
                                      className="ml-1 text-gray-500 px-2 py-1 rounded-md text-sm"
                                      onClick={() => setIsAddingNewTag(false)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <button onClick={() => setShowCustParamModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}>Cancel</button>
                      <button onClick={saveCustParameter} className={`px-4 py-2 text-white rounded-lg ${(!custParamManualQuery || custParamQueryError) ? 'bg-gray-500 hover:bg-gray-700' : 'bg-indigo-600 hover:bg-indigo-700'}`} style={{height: "40px", borderRadius: "4px"}}>Save</button>
                    </div>
                  </div>
                )}

                </div>
                <div hidden={custInnerTab !== 'subparameters'}>
                {/* Subparameters Section */}
                <div className='p-6 border-b border-gray-200 shadow-md rounded-lg'>
                  <h2 className="text-lg font-medium text-gray-800 mb-3">Subparameters</h2>
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex-1 min-w-[12rem]"><ConfigurationTableSearch className="" label="Search customer subparameters by name" value={tableSearch.customerSubparameters} onChange={value => setTableSearch({ ...tableSearch, customerSubparameters: value })} /></div>
                    <div className="flex flex-wrap items-center gap-4 shrink-0">
                      <label className="flex items-center whitespace-nowrap text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={showArchivedCustSubparameters}
                          onChange={(e) => setShowArchivedCustSubparameters(e.target.checked)}
                          className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Show archived subparameters
                      </label>
                      <button className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}} onClick={openAddCustSubparam}>Add Subparameter</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="configuration-table divide-y divide-gray-200">
                      <colgroup>
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "85px" }} />
                        <col style={{ width: "14%" }} />
                        <col />
                        <col style={{ width: "90px" }} />
                        <col style={{ width: "160px" }} />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Definition / Logic</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kind</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Options</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parameterTableRows(filterTableRows(custSubparameters, showArchivedCustSubparameters), tableSearch.customerSubparameters).map(sp => (
                          <tr key={sp.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-wrap">{sp.display_name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="truncate" title={sp.logic || sp.instruction || ''}>
                                {sp.logic ? <code className="bg-gray-100 px-2 py-1 rounded">{sp.logic.length > 100 ? sp.logic.slice(0, 100) + '...' : sp.logic}</code> : sp.instruction || '-'}
                              </div>
                              {sp.variables_used?.length > 0 && <div className="mt-1 text-xs text-gray-500">Variables Used: {sp.variables_used.join(', ')}</div>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {sp.system === 2 ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Client</span> :
                               sp.system ? <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">System</span> :
                               sp.logic || sp.subparameter_type === 'logic_based' ? <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">Logic</span> :
                               sp.subparameter_type === 'classifier' ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Classifier</span> :
                               <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">LLM</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sp.output_type || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-wrap">{sp.output_type !== 'Number' ? <ConfigurationOptions options={sp.possible_values} /> : '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className={`px-2 py-1 rounded-full text-xs ${!sp.status || sp.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {sp.status === 'archived' ? 'Archived' : sp.status === 'inactive' ? 'Inactive' : 'Active'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {sp.system ? (
                                <span className="text-gray-400">System Parameter</span>
                              ) : sp.status === 'archived' ? (
                                <button type="button" className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" title="Restore" onClick={() => handleRestoreCustSub(sp)}>
                                  <TbRestore className="h-4 w-4" />
                                </button>
                              ) : (
                                <div className="inline-flex items-center justify-end gap-3">
                                  <button type="button" className="text-indigo-600 hover:text-indigo-900 text-md" onClick={() => openEditCustSubparam(sp.id)}>
                                    <FaRegEdit/>
                                  </button>
                                  <button type="button" className="text-red-600 hover:text-red-900" title="Archive" onClick={() => openArchiveModal('cust_subparameter', sp)}>
                                    <Archive className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {showCustSubparamModal && (
                  <div ref={custSubparamFormRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4" style={{width: "85%", margin: "1rem auto"}}>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-medium text-gray-800">{editingCustSubparam ? 'Edit Customer Subparameter' : 'Add Customer Subparameter'}</h3>
                      <button onClick={() => setShowCustSubparamModal(false)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{color: "slategray"}}/></button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                        <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md global-input" value={custSubparamName} onChange={(e) => setCustSubparamName(e.target.value)} />
                      </div>
                      
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Variables Used</label>
                        <div className="flex gap-2 mb-2 relative">
                          <input
                            ref={custSubparamInputRef}
                            type="text"
                            value={custSubparamSelectedVar}
                            onChange={(e) => { setCustSubparamSelectedVar(e.target.value); setCustSubparamShowDropdown(true); }}
                            onFocus={() => setCustSubparamShowDropdown(true)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustVarToSubparam(); } }}
                            className="flex-1 px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                            placeholder="Enter variable name"
                          />
                          <button
                            onClick={() => addCustVarToSubparam()}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                          >
                            Add
                          </button>
                          {custSubparamShowDropdown && filteredCustVars.length > 0 && (
                            <div
                              ref={custSubparamDropdownRef}
                              className="absolute top-full left-0 right-24 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto"
                            >
                              {filteredCustVars.map((name) => (
                                <div
                                  key={name}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                  onClick={() => { setCustSubparamSelectedVar(name); setCustSubparamShowDropdown(false); }}
                                >
                                  {name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {(() => {
                          const convParams = (custSubparamSelectedVars || []).filter(n => convParamVars.includes(n));
                          const systemCustSubs = (custSubparamSelectedVars || []).filter(n => systemCustSubVars.includes(n));
                          const prevCustSubs = (custSubparamSelectedVars || []).filter(n => n.startsWith('prev_') && custSubVars.includes(n.slice(5)));
                          return (
                            <>
                              {convParams.length > 0 && (
                                <>
                                  <div className="text-xs font-medium text-gray-700 mb-1">Conversation parameters:</div>
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {convParams.map((name) => (
                                      <div key={`conv_${name}`} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                                        <span className="text-sm text-gray-700">{name}</span>
                                        <button onClick={() => removeCustVarFromSubparam(name)} className="text-gray-500 hover:text-gray-700">
                                          <FaTimes style={{color: "slategray"}}/>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              {systemCustSubs.length > 0 && (
                                <>
                                  <div className="text-xs font-medium text-gray-700 mb-1">System customer subparameters:</div>
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {systemCustSubs.map((name) => (
                                      <div key={`sys_${name}`} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                                        <span className="text-sm text-gray-700">{name}</span>
                                        <button onClick={() => removeCustVarFromSubparam(name)} className="text-gray-500 hover:text-gray-700">
                                          <FaTimes style={{color: "slategray"}}/>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              {prevCustSubs.length > 0 && (
                                <>
                                  <div className="text-xs font-medium text-gray-700 mb-1">Previous customer subparameters:</div>
                                  <div className="flex flex-wrap gap-2 mb-3">
                                    {prevCustSubs.map((name) => (
                                      <div key={`prev_${name}`} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                                        <span className="text-sm text-gray-700">{name}</span>
                                        <button onClick={() => removeCustVarFromSubparam(name)} className="text-gray-500 hover:text-gray-700">
                                          <FaTimes style={{color: "slategray"}}/>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </>
                          );
                        })()}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
                            <ModernTimeDropdown
                              width='w-full'
                              placeholder="Select Output Type"
                              value={custSubparamType === 'Date and Time' ? 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)' : custSubparamType}
                              options={['Yes/No/NA', 'Categorical - Single Value', 'Categorical - Multiple Values', 'Number', 'Text', 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)']}
                              onChange={(e) => {
                                const label = 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)';
                                setCustSubparamType(e.target.value === label ? 'Date and Time' : e.target.value);
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-2">
                            <label className="block text-sm font-medium text-gray-700">Logic</label>
                            <div className="relative">
                              <Info size={16} className="text-gray-400" />
                            </div>
                          </div>
                          <button onClick={() => verifyCustSubparamQuery()} className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300" style={{height: "32px", borderRadius: "4px"}}>Validate Logic</button>
                        </div>
                        {!logicLoading && (
                          <div className="mb-2">
                            <div className='flex items-center gap-2 mt-2'>
                              <p style={{fontSize: "12px", fontStyle: "italic", color: "gray", width: "60%"}}>Output can be text or number</p>
                              <p style={{fontSize: "12px", fontStyle: "italic", color: "gray", width: "40%"}}>Explanation</p>
                            </div>
                            <div className='flex items-start gap-2'>
                              <textarea className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" rows="6" placeholder="Enter your logic here..." value={custSubparamManualQuery} onChange={(e) => setCustSubparamManualQuery(e.target.value)} style={{border: custSubparamQueryError ? "1px solid red" : "", width: "60%", height: "180px", overflow: "auto", fontSize: "14px"}} />
                              <textarea className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input" value={custSubparamLogicExplanation} onChange={(e) => setCustSubparamLogicExplanation(e.target.value)} rows="6" style={{ width: "40%", height: "180px", overflow: "auto", fontSize: "14px", whiteSpace: "pre-line"}} />
                            </div>
                            {custSubparamQueryError && (<p className="mt-2 text-sm text-red-600">{custSubparamQueryError}</p>)}
                          </div>
                        )}
                        {logicLoading && (
                          <div className="flex justify-center items-center my-4" style={{ flexDirection: "column" }}>
                            <div className="loader"></div>
                            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                          </div>
                        )}
                      </div>
                      
                    </div>
                    <div className="flex justify-end gap-3 mt-4">
                      <button onClick={() => setShowCustSubparamModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}>Cancel</button>
                      <button onClick={saveCustSubparam} className={`px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed`} style={{height: "40px", borderRadius: "4px"}}>Save</button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            )}
          </div>}
          {showSampleTestModal && (
            <div>
              <div style={{position: "fixed", left: "2%", top: "6.5%", width: "96%", height: "90%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px",
                zIndex: "999999999", padding: "0 15px"}}>
                <SampleTestModal clientID={client_id} serviceID={service_id} emailID={email} moduleName='custom_module' onClose={handleCloseSampleTestModal}/>
              </div>
            </div>
          )}
          {showArchiveModal && archiveItem && (() => {
            const blockers = getArchiveBlockers(archiveType, archiveItem);
            const archiveLabel = archiveType?.includes('subparameter') ? 'subparameter' : 'parameter';
            return (
              <div style={{ zIndex: "9999999999", position: "fixed", top: "0", left: "0", height: "100%", width: "100%", background: "rgba(0,0,0, 0.5)" }}>
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
                    <div className="flex justify-between items-center p-4 pb-2">
                      <h2 className="text-xl text-gray-700" style={{ fontWeight: "550" }}>Are you sure?</h2>
                      <button onClick={resetArchiveModal} className="text-gray-500 hover:text-gray-700">
                        <FaTimes style={{ color: "slategray" }} />
                      </button>
                    </div>
                    <div className="p-6 pt-2">
                      <p className="text-gray-500 text-sm text-left">
                        This {archiveLabel} will be archived.
                      </p>
                      {blockers.systemBlocked && (
                        <div className="mt-4 text-sm text-left text-gray-700">
                          <p className="font-medium text-gray-800">System subparameters cannot be archived</p>
                        </div>
                      )}
                      <div className="mt-4 space-y-3 text-sm text-left text-gray-700">
                        {blockers.sections.map((section) => (
                          <div key={section.title}>
                            <p className="font-medium text-gray-800">{section.title}</p>
                            {section.items.length === 0 ? (
                              <p className="text-gray-500">None</p>
                            ) : (
                              <ul className="mt-1 list-disc list-inside">
                                {section.items.map((entry) => (
                                  <li key={`${section.title}-${entry.id}`}>{entry.label}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 p-6 pt-6">
                      <button onClick={resetArchiveModal} className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" style={{ height: "40px", borderRadius: "4px" }}>
                        No
                      </button>
                      <button
                        onClick={confirmArchive}
                        className={`px-6 py-2 rounded-lg ${blockers.hasBlockers ? 'bg-gray-500 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
                        style={{ height: "40px", borderRadius: "4px" }}
                        disabled={blockers.hasBlockers}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          {loading && (
            <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
            </div>
          )}
        

      </div>
    </div>
    );
  }

  // Preview Screen
  return (
    <div className="p-3">
      <div className=" mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Custom Module</h1>
            <p className="text-gray-500 text-sm mt-1">Sample results</p>
          </div>
          <div className="flex space-x-3">
            <button
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none" style={{height: "40px", borderRadius: "4px"}}
              onClick={() => { setShowPreview(false); setExpandedSample(null); setSamples([]); }}
            >
              Go Back
            </button>
            <button onClick={() => { generateSamples(0); setSamples([]); setExpandedSample(null);}} className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none" style={{height: "40px", borderRadius: "4px"}}>
              Run Different Sample
            </button>
            <ConfigurationDiffButton
              title="Custom Module Changes"
              originalConfig={() => configWithExecutionSettings(getLoadedConfigForDiff(), loadedModuleExecution)}
              modifiedConfig={() => configWithExecutionSettings(getCurrentConfigForDiff(), moduleExecution)}
              triggerLabel="Deploy"
              showTriggerIcon={false}
              disabled={loading}
              triggerClassName={`px-4 py-1 h-10 ${loading ? 'bg-gray-500 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'}  text-white rounded  focus:outline-none`}
              onNext={() => setShowDeployConfirmation(true)}
            />
          </div>
        </div>
        <DeployConfirmationModal
          isOpen={showDeployConfirmation}
          confirmationText={`Changes will be deployed in ${client_id || 'N/A'}, ${service_id || 'N/A'} and ${module_name || 'custom_module'}`}
          onClose={() => setShowDeployConfirmation(false)}
          onConfirm={(versionDescription) => {
            setShowDeployConfirmation(false);
            deployModule(versionDescription);
          }}
        />

        <div style={{height: "75vh", overflow: "auto"}}>
        {!loading && Array.isArray(samples) && samples.map((sample, index) => (
          <div key={sample.call_id} className="border-b border-gray-200 last:border-0 my-2">
            <div  onClick={() => setExpandedSample(expandedSample === index ? null : index)} className='p-4 border-b rounded-lg border-gray-200 bg-gray-50 cursor-pointer'>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <span className="font-medium text-gray-800">{sample.name}</span>
              </div>
              <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSample === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
            <div className="text-sm text-gray-600 mt-1 capitalize" style={{fontSize: "12px"}}>
                        ID: {sample.call_id} | Time: {sample.call_time} | Employee: {sample.employee_name} | Record Type: {sample.record_type}
                      </div>
            </div>
            {expandedSample === index && (
              <div className="bg-white rounded-lg shadow p-4 overflow-hidden border flex">
                <div className="border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.65"}}>
                            <div className="p-2 bg-gray-50 border-b border-gray-200 cursor-pointer flex items-center" style={{justifyContent: "space-between"}}>
                              <div className="flex items-center">
                                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
                                </svg>
                                <h3 className="font-medium text-gray-800">Output</h3>
                              </div>
                            </div>
                            <div className="p-4">
                  {sample.parameters && (
                    <FeedbackSection key="parameters" showMoreMap={showMoreMap} toggleShowMore={toggleShowMore} source_click={(e)=>handleSourceClick(e, sample.segments)}
                    dataKey="Custom Module" metadata={sample.parameters?.metadata}
                    data={sample.parameters?.parameters || sample.parameters} maxHeight="45vh" />
                  )}
                </div>
                </div>
                {/* Audio Section */}
                <div className="border border-gray-200 rounded-lg overflow-hidden ml-2" style={{flex: "0.35"}}>
                            <div className="p-2 bg-gray-50 border-b border-gray-200 flex items-center">
                              <div className="flex items-center">
                              {sample.record_type === 'call' ? (
                  <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                </svg>
                  ) : (<IoChatbubbleOutline style={{marginRight: "0.5rem", height: "30px"}}/>)}
                                <h3 className="font-medium text-gray-800">{sample.record_type === 'call' ? 'Audio' : 'Chat' }</h3>
                              </div>
                            </div>
                            {sample.record_type === 'call' && <div className="p-2 bg-white">
                              <audio controls key={sample.record_link} ref={audioRef} onTimeUpdate={()=>handleAudioTimeUpdate(sample.segments)} style={{ width: "96%", margin: "0 1rem", height: "40px" }}>
                                <source src={sample.record_link} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>}
                            <hr/>
                            <div style={{fontSize: "13px", margin: "10px", overflow: 'auto', height: "40vh"}}>
                            {
                              sample.record_type === 'call' && sample.segments.map((item, idx)=> {
                                        const isSelected = selectedSegmentIdx === idx;
                                        return (
                                          <div key={`segment_${idx}`} ref={(el) => (segmentRefs.current[idx] = el)} onClick={()=>toggleSegment(item, idx, sample.record_link)} style={{marginBottom: "10px", borderLeft: isSelected ? "4px solid #6f6ffe":"", background: isSelected ? "rgba(249, 250, 251, 1)":"",
                                            padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                              <div style={{fontSize: "14px", color: "grey"}}>{item.speaker}</div>
                                              <div style={{fontSize: "14px", color: "grey"}}>{formatTime(item.start_time)}</div>
                                            </div>
                                            <div style={{fontSize: "15px"}}>{item.text}</div>
                                          </div>
                                        )
                                      })
                                    }
                            {
                              sample.record_type === 'chat' && sample.segments.map((item, idx)=> {
                                return (
                                  <div key={`segment_${idx}`}
                                  data-message-id={item.message_id}
                                  style={{marginBottom: "10px", borderLeft: getBorderColor(item.actor_type), background: getBackgroundColor(item.actor_type),
                                    padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                      <div style={{fontSize: "10px", display: "flex", alignItems: "center"}}>
                                        <span className='' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.actor_type}</span>
                                        {item.message_type !== 'normal' && <span className=' ml-1' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.message_type}</span>}
                                      </div>
                                      <div style={{fontSize: "13px", color: "grey"}}>{formatEpochTime(item.timestamp)}</div>
                                    </div>
                                    <div style={{fontSize: "14px"}}><span style={{whiteSpace: "pre-line"}}>{item.content?.text?.content} <br/></span></div>
                                    {item.content.image && item.content.image.url && 
                                      <div style={{fontSize: "15px", whiteSpace: "pre-line"}}>
                                        <br/>
                                       Image Shared - {item.content.image.url}
                                      </div>
                                      }
                                    {
                                      item.content.buttons && item.content.buttons.length > 0 && (
                                        <div>
                                          <br/>
                                          {item.actor_type === 'user' ? (
                                            <span>Button - Selected Option - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                          ) : (
                                            <span>Button - Options - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                          )}
                                        </div>
                                      )
                                    }
                                  </div>
                                )
                                      })
                                    }
                            </div>
                          </div>
                          
              </div>
            )}
          </div>
        ))}
        {
          loading && (<div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
            <div className="loader"></div>
            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
          </div>)
        }
        </div>
      </div>
    </div>
  );
};

export default CustomModuleConfigure;
